/**
 * Add a source: say where the data is, confirm what the fields mean, save.
 *
 * **One tab, two ways in.** *Choose a source* picks a publisher the portal
 * already knows and composes its Feeds from a few declared values; *Feed URL*
 * pastes an address. The menu leads because the point of this screen is that
 * API addresses are not a user's problem — PFF needs thirteen of them, each
 * carrying a report slug, a division and an explicit twenty-one-element week
 * list. Pasting a URL still does exactly what it did, and remains the only path
 * for any source that never gets a template.
 *
 * The composed path renders **entirely from the template's declaration**. There
 * is no branch on which publisher was chosen anywhere in this file or in
 * `TemplateParameters`, which is what makes a second publisher cost a
 * declaration on the server and nothing here.
 *
 * The field-review step is the point of the whole portal, so three things it
 * does are deliberate rather than incidental. Scope is chosen once for the
 * profile instead of per field — the difference between classifying a few
 * hundred fields and classifying over a thousand. Unrecognised fields arrive
 * already answered, so a profile can be saved as soon as the statistics that
 * matter resolve: a number nobody recognises is *carried* into the raw file
 * untouched, and only a label nobody recognises is *ignored*, because a column
 * of names no ranking can read is the one thing worth discarding. And a
 * statistic the dictionary does not hold can be added from the row that needs
 * it, because the alternative was the Stat Dictionary tab and then starting
 * this screen over — the field table is local state, so every classification
 * made so far went with it (ADR-0029).
 */

import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  api,
  applicableParameters,
  feedInputFrom,
  feedsToScan,
  signInSettled,
  sportsOffered,
  templateDefaults,
  type CredentialSummary,
  type Decoding,
  type Expansion,
  type DiscoveredFeed,
  type DiscoveredField,
  type DiscoveryResult,
  type Pagination,
  type RepeatedIdentity,
  type Role,
  type SourceProfileInput,
  type TemplateValues,
} from "../api";
import {
  chooseSport as chooseLeagueSport,
  credentialNote,
  leagueFor,
  leagueSportOptions,
  narrowLeagues,
  selectsFor,
  sourceFor,
  sourceNote,
} from "../league-menu";
import { composedSourceName } from "../source-name";
import {
  anchorForField,
  bindingProblems,
  firstMarkedRow,
  markedRows,
  problemsFromRefusal,
  refusalNotice,
  rowSignature,
  type ProblemRow,
} from "../binding-problems";
import {
  autoMatchReports,
  childRowKey,
  draftsForField,
  expansionHazard,
  problemRowsForField,
  publishedFieldsByFeed,
} from "../field-reports";
import { MAX_ATTEMPTS, withRetry } from "../retry";
import { awaitDiscoveryJob } from "../discovery-job";
import { weightNotice } from "../stat-weight";
import { REPEATED_IDENTITY_DEFAULT, repeatedIdentityFor } from "../repeated-identity";
import { keys, POLL_RUN_MS } from "../queries";
import { Layout } from "../components/Layout";
import { ClientIdentityField } from "../components/ClientIdentityField";
import { DEFAULT_PAGINATION, PaginationChoice } from "../components/PaginationChoice";
import { TemplateParameters } from "../components/TemplateParameters";
import { Combobox, type ComboboxRow } from "../components/Combobox";
import { StatGroupPreview } from "../components/StatGroupPreview";
import { InfoIcon } from "../components/icons";
import { labelsFor } from "../position-exclusions";
import { unionedLabels } from "../position-map";
import {
  HintedHeading,
  NewStatisticForm,
  NewStatisticRow,
  ORDER_HINT,
  OrderCell,
  ProblemNote,
  RepeatedIdentityField,
  ROLE_OPTIONS,
  SampleCell,
  StatisticCell,
  WeightCell,
} from "../components/FieldMapping";
import {
  Badge,
  Banner,
  Button,
  Card,
  Code,
  cx,
  Field,
  Label,
  Muted,
  PillGroup,
  ScrollPanel,
  Select,
  SelectCompact,
  Spinner,
  TableScroll,
  TextInput,
} from "../components/ui";

/**
 * Which way in. The menu is the default: typing an address is the fallback for
 * a publisher nobody has written a template for, not the ordinary case.
 */
type Route = "template" | "url";

const ROUTES: { value: Route; label: string }[] = [
  { value: "template", label: "Choose a source" },
  { value: "url", label: "Feed URL" },
];

interface FieldChoice {
  role: Role;
  statKey: string | null;
  /**
   * What this statistic is worth in the Impact Factor (ADR-0040). Null is
   * nothing declared, which weighs 1.
   *
   * Carried on the choice rather than rebuilt at Save, because a template may
   * compose one: MLS NEXT Pro's mapping is curated — 27 of the 70 statistics its
   * profile ranks carry a weight somebody chose — and a Save that reconstructed
   * Bindings from role and stat key alone would drop all 27 silently.
   */
  weight: number | null;
  /**
   * Where this field's column sits in the published header (ADR-0058). Null is
   * nothing declared, which sorts after everything declared.
   *
   * Offered here and not only on the Fields screen because this is where
   * somebody first meets a source's fields, and because the order the file
   * publishes is a decision about the file rather than about a review done
   * later — the same reason the per-report control was brought forward onto
   * this screen.
   */
  sortOrder: number | null;
  /**
   * How to read the value: `{}` for an ordinary scalar, `{type: "duration"}`
   * for an `HH:MM:SS` playing time, `{type: "join", with: [...]}` for a name the
   * publisher split across fields. Composed, never typed here — and carried for
   * the same reason the weight is.
   */
  parseRule: Record<string, unknown>;
}

/**
 * What has to be true before a source can be saved.
 *
 * The rules live in one list rather than in the Save button's `disabled`
 * expression, because a disabled button is a dead end: it states that something
 * is wrong without saying what, and the reader is left comparing the form
 * against a rule they cannot see. Pressing Save always does something — either
 * it saves, or it says which field is at fault and takes you to it.
 *
 * Order is screen order, so "the first problem" is also the topmost one.
 */
type ErrorKey = "name" | "league" | "identity";

type Errors = Partial<Record<ErrorKey, string>>;

/**
 * Somewhere a failed Save can send the reader.
 *
 * The rules above are three of them; `submit` is the fourth, and it is not a
 * rule at all — it is where the server's own refusal is shown, next to the
 * button that provoked it.
 */
type AnchorKey = ErrorKey | "submit" | "fetch";

/**
 * Where a failed Save travels, which is not always a control.
 *
 * A problem with a *field* belongs at its row in the table, and the table's
 * rows are discovered rather than declared — so they cannot be a key in the
 * fixed record of refs the three rules use. Naming the two cases is what lets
 * the one effect serve both.
 */
type Travel = { kind: "anchor"; key: AnchorKey } | { kind: "row"; path: string };

const REQUIRED: { key: ErrorKey; message: string }[] = [
  { key: "name", message: "Give this source a name, so it can be told apart from the others." },
  { key: "league", message: "The league is required. It appears in the name of every file this source writes." },
  {
    key: "identity",
    message:
      "Mark at least one field as the Identity Key or the player name, so players can be told apart.",
  },
];

export function AddSource() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [route, setRoute] = useState<Route>("template");
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  /**
   * Whether the name on screen is somebody's or the form's own suggestion.
   *
   * It decides whether a recompose may rewrite the name, and it is the whole
   * of the fix for a source called *PFF — Player Statistics (FBS, Reg + Post)*
   * sitting in the `fcs` League. The name used to be suggested once, when the
   * fields were fetched, and guarded by `if (!name)` — so changing the division
   * afterwards moved the League, moved thirteen Feed addresses and left the
   * name naming the division nobody chose.
   *
   * A boolean rather than comparing against the last suggestion, because the
   * two are different questions and only this one has a safe answer when
   * somebody types a name that happens to match: typing is a decision, and the
   * form has no business undoing it the next time a parameter moves.
   */
  const [nameIsTyped, setNameIsTyped] = useState(false);
  const [league, setLeague] = useState("");
  const [sport, setSport] = useState("football");
  const [season, setSeason] = useState(new Date().getFullYear() + 1);
  const [scope, setScope] = useState<string>("");
  /**
   * Every Stat group's fields as the last read *across all groups* found them.
   *
   * Kept apart from the discovery result because choosing a group re-reads the
   * source for that group alone, and the popup that explains the choice would
   * otherwise shrink to the one group already chosen.
   */
  const [groupFields, setGroupFields] = useState<{ url: string; fields: DiscoveredField[] } | null>(
    null,
  );
  const [groupsOpen, setGroupsOpen] = useState(false);
  /**
   * What a repeated Identity Key inside one Feed means.
   *
   * `fail` until a template says otherwise, which is the server's default and
   * the answer every publisher deserves until one is measured repeating rows.
   * Seeded from `composed.defaults` by the prefill effect below, on the same
   * terms as the Record Path: a template that declares it is describing the
   * publisher, and a reviewer may still disagree before saving.
   */
  const [repeatedIdentity, setRepeatedIdentity] =
    useState<RepeatedIdentity>(REPEATED_IDENTITY_DEFAULT);
  const [recordPath, setRecordPath] = useState<string>("");
  /** Whether the players' list is being typed rather than picked from the menu. */
  const [typingRecordPath, setTypingRecordPath] = useState(false);
  const [pagination, setPagination] = useState<Pagination>(DEFAULT_PAGINATION);
  // The Client Identity, blank for the portal's honest default (ADR-0015).
  // Reachable on the pasted-URL route because that is the route with no
  // template to set it, and therefore the only route that could otherwise meet
  // an identity refusal with no control that fixes it (ADR-0018).
  const [userAgent, setUserAgent] = useState("");

  /**
   * Which league is being added, as `league_menu`'s key — a `league_slug`.
   *
   * **Asked before the publisher, which is the change this screen exists
   * around.** The client reported the consequence of the old order rather than
   * the order itself: they did not know which source carried the league they
   * wanted, so finding out meant opening each of ten sources and reading its
   * menu. Two publishers carry NCAA Division II, and nothing on the screen said
   * so.
   *
   * **Named `menuLeague`, beside `menuSport` and for exactly its reason.**
   * There is already a `league` on this screen — the one the profile is *saved*
   * with, which on this route is composed rather than chosen, arrives in
   * `defaults`, and is edited in step 2. This one is a way of finding a
   * template: nothing reads it once the template is chosen, and nothing saves
   * it. Letting the two share a name would have made every later reader guess
   * which question was being asked.
   */
  const [menuLeague, setMenuLeague] = useState("");
  /** What has been typed into the League box, cleared whenever it closes. */
  const [leagueQuery, setLeagueQuery] = useState("");
  const [templateKey, setTemplateKey] = useState("");
  const [values, setValues] = useState<TemplateValues>({});
  /**
   * Which sport the Source menu is narrowed to, blank for all of them.
   *
   * Separate state from `sport`, and deliberately not derived from it. `sport`
   * is a property of the profile being saved — the folder its files land in —
   * and on this route it is *composed*, not chosen: it is settled by the league
   * and arrives with the first composition. This one is a property of the menu
   * above, held before any of that exists and read by nothing that gets saved.
   * Tying them together would mean a filter that changes what a profile
   * publishes, or a composition that silently re-narrows the menu it came from.
   *
   * It narrows the **League** menu now rather than the Source menu, which is
   * the same job one question earlier. It still reaches the parameter controls
   * below through `optionsFor`, and that still matters for the same reason:
   * ESPN is one template over two sports, so a football filter that left MLS in
   * its League parameter would be a filter answering half the question.
   */
  const [menuSport, setMenuSport] = useState("");

  // The Credential Set this source is being added around, once it has been
  // signed in with. It exists before the profile does, which is the whole
  // point: a source that sells columns rather than access answers an anonymous
  // read with every player and none of the statistics, so reading its fields
  // before signing in means reviewing the wrong table.
  const [credentialId, setCredentialId] = useState<string | null>(null);
  const [signIn, setSignIn] = useState({ username: "", password: "", loginUrl: "" });
  const [bearerToken, setBearerToken] = useState("");

  const templates = useQuery({ queryKey: keys.templates, queryFn: api.listTemplates });
  const template = templates.data?.find((entry) => entry.key === templateKey);

  /**
   * The catalogue read by league: fifty-four rows, each naming its publishers.
   *
   * A second query rather than something derived from `templates`, because only
   * the server can know which parameter on which template reaches which league
   * — the key is `division` on four templates, `league` on three, `gender` on
   * one, and absent on the two single-league publishers. Deriving it here would
   * be the frontend guessing at a publisher's shape, which is what ADR-0018
   * says the Template route exists to prevent. It composes nothing, so it
   * answers immediately and reaches no publisher.
   */
  const leagues = useQuery({ queryKey: keys.leagues, queryFn: api.listLeagues });

  /**
   * The Sport filter over the League menu, and what it leaves showing.
   *
   * Every answer is computed in `league-menu.ts` rather than here, because the
   * frontend runs its tests as bare `node` over `tests/` and a rule inside a
   * component is a rule nothing can assert.
   *
   * `menuOptions` is empty when the menu spans one sport or none, which the
   * caller reads as "render no control" — the convention `sportsOffered` set.
   */
  const menuOptions = useMemo(() => leagueSportOptions(leagues.data), [leagues.data]);
  const leagueRows = useMemo(
    () => narrowLeagues(leagues.data ?? [], { query: leagueQuery, sport: menuSport }),
    [leagues.data, leagueQuery, menuSport],
  );
  /**
   * How many leagues the menu is a menu *of*, which is after the sport facet
   * and before the query.
   *
   * The two narrow the same list and are not the same kind of thing. The facet
   * changes what is being searched; the query searches it. A count line that
   * took its denominator from the whole menu read "2 of 54 match" over a
   * soccer-filtered list holding twenty — a true sentence about a list nobody
   * was looking at.
   */
  const leagueTotal = useMemo(
    () => narrowLeagues(leagues.data ?? [], { query: "", sport: menuSport }).length,
    [leagues.data, menuSport],
  );
  /** The league's row, and the publisher of it this form is building against. */
  const chosenLeague = leagueFor(leagues.data, menuLeague);
  const chosenSource = sourceFor(leagues.data, menuLeague, templateKey);

  /** What the form settled that the reader did not, or null. See `sourceNote`. */
  const menuNote = sourceNote(leagues.data, menuLeague, templateKey);

  /**
   * The League menu as the combobox draws it.
   *
   * **Leads with the printed name and keeps the canonical one underneath.**
   * The first draft led with `league`, on the argument that a name invented
   * here would be a second name for a league appearing nowhere else. The
   * argument was about the name's provenance and the screen settled it: thirty
   * of the fifty-four canonical names are Cerebro's and are slugs, so the menu
   * opened on `adidas_3ssb_15u` over `adidas_3ssb_16u` and read as a database
   * column rather than a list of leagues. The name is still not invented here —
   * it is the publisher's own, or a declared one where neither that nor the
   * league's name reads alone (`LeagueOffer.display`).
   *
   * The canonical name still travels, in the note, on the nine rows where it
   * differs from the title. It is what the Sources table will print for this
   * profile and what its published files are named, so a menu that replaced it
   * outright would be hiding the string the operator ends up working with —
   * and repeating it where it is already the title would be saying everything
   * twice.
   *
   * The badge counts sources only where there is more than one, since a
   * badge on every row is a badge saying nothing.
   *
   * **Publishers are named once each, and the badge still counts sources.** The
   * two differ since ADR-0059: PFF reaches FBS twice, over its legacy browser
   * API and over its developer API, so the unfiltered list read "Pro Football
   * Focus, Pro Football Focus" and the row answered the question "who
   * publishes this" by saying one name twice. The count is deliberately *not*
   * de-duplicated with it — "2 sources" is true and is what makes the dropdown
   * worth opening, where "1 publisher" would say there was nothing to choose.
   */
  const leagueOptions = useMemo<ComboboxRow[]>(
    () =>
      leagueRows.map((entry) => ({
        value: entry.key,
        label: entry.display,
        note: [
          entry.sport,
          [...new Set(entry.sources.map((one) => one.publisher))].join(", "),
          ...(entry.display === entry.league ? [] : [entry.league]),
        ].join(" · "),
        badge: entry.sources.length > 1 ? `${entry.sources.length} sources` : undefined,
      })),
    [leagueRows],
  );

  /**
   * The Feeds these values produce, re-asked whenever they change.
   *
   * A query rather than a mutation because composing creates nothing, and
   * because keying it on the parameters is what makes changing the season
   * visibly rewrite every address rather than leaving the previous ones on
   * screen next to a new season number.
   */
  const composition = useQuery({
    queryKey: keys.templateComposition(templateKey, values),
    queryFn: () => api.composeTemplate(templateKey, values),
    enabled: route === "template" && templateKey !== "" && Object.keys(values).length > 0,
    retry: false,
  });

  const composed = route === "template" ? composition.data ?? null : null;

  /**
   * The League box kept honest about what the form is actually composing.
   *
   * The league menu seeds a parameter and then **stops being in charge of it**.
   * That is deliberate — picking a league is a way to find a source, never a
   * constraint on the profile being built, so PFF's Division control stays
   * live underneath and moving it to FCS is a legitimate thing to do. What is
   * not legitimate is the box above still reading `ncaa_d2` while the
   * composition, the step-2 League field and the file that gets published all
   * say `fbs`. Two controls disagreeing about which league this is, with the
   * one the operator is *not* looking at being the one that wins at Save.
   *
   * So the composition is the authority and this follows it. Matched on the
   * league name rather than on a slug, because both sides are the string the
   * composer emits — `league_menu` groups on `league_slug` of exactly this —
   * and a second slugging rule in the frontend would be a rule that drifts.
   *
   * An unrecognised league clears the box rather than leaving the old answer
   * standing: the menu is a view of the templates, so a composed league absent
   * from it means the two are out of step, and an empty control says that where
   * a stale one would hide it.
   */
  useEffect(() => {
    if (route !== "template" || composed === null) return;
    const league = composed.defaults.league;
    if (typeof league !== "string" || league === "") return;
    if (chosenLeague?.league === league) return;
    const matched = (leagues.data ?? []).find((entry) => entry.league === league);
    setMenuLeague(matched?.key ?? "");
  }, [route, composed, chosenLeague, leagues.data]);

  /**
   * The sports the Sport control offers, and whether it is shown at all.
   *
   * Exactly the sport this configuration composes, for a template — one option,
   * not the publisher's whole range. ESPN spans football and soccer, but an
   * ESPN profile *on the NFL* fetches football and nothing else, and offering
   * soccer beside it offers an answer its own Feeds contradict. Empty until a
   * composition has returned, which is the caller's signal to render nothing:
   * at that moment the sport is not unknown-and-choosable, it is simply not
   * established yet, and a required control showing a guess is worse than one
   * that has not appeared. See `sportsOffered`.
   */
  const offeredSports = useMemo(
    () => sportsOffered(route, composed),
    [route, composed],
  );

  /**
   * A sport outside what is on offer is not left standing.
   *
   * The control's value starts at `football`, which is right for a pasted URL
   * and wrong for five of the seven templates — and a `<select>` whose value
   * matches none of its options renders **blank**. The prefill below sets the
   * same value from the same composition, so this is the belt to its braces:
   * it also catches a template switched *away* from, where the prefill is
   * keyed on the answer and deliberately does not re-fire.
   *
   * Skipped while the list is empty, because nothing is on screen to be wrong.
   */
  useEffect(() => {
    const [first] = offeredSports;
    if (first !== undefined && !offeredSports.includes(sport)) setSport(first);
  }, [offeredSports, sport]);
  // The address the *credential* is proved against, and the one whose hostname
  // names an unnamed source. One is enough for both: a session earned at a
  // publisher is a session at every address of that publisher.
  //
  // It is **not** the address the field scan reads. That was what this comment
  // used to say, on the reasoning that "a publisher's Feeds differ by which
  // players they carry, not by their shape" — true of neither PFF nor ESPN, and
  // exactly backwards for Wyscout, whose Stat Filters differ by column and
  // whose conferences do not. Reading `feeds[0]` alone offered 8 of Wyscout's
  // 91 statistics and 1 of PFF's 13 reports, with nothing on either screen
  // saying so. See `scanFeeds`.
  const sourceUrl = composed ? composed.feeds[0]?.url ?? "" : url;
  /**
   * Every address the field scan reads: one per distinct shape.
   *
   * Seven for a Wyscout profile of a hundred and fifty-four Feeds, thirteen for
   * PFF, one for a pasted URL. All of them read the same population under
   * different projections, which is what makes merging their fields on `path`
   * sound rather than a guess.
   */
  const scanFeeds = useMemo(
    () => (composed ? feedsToScan(composed.feeds) : []),
    [composed],
  );

  /**
   * Opening the form on a template, with the league's own choice already in it.
   *
   * `seed` is what the league menu settled — `{division: "ncaa-d2"}` for PFF's
   * Division II, `{}` for a publisher with one league. It reaches
   * `templateDefaults` rather than being merged over the result, so that what
   * the seeded choice *implies* reaches the parameters below it: ESPN's soccer
   * leagues carry a different season from its football ones, and a merge
   * afterwards would leave football's year in the Season box beside a soccer
   * league with every control looking filled in.
   *
   * The Sport filter still decides everything the seed does not, which is why
   * both are passed: picking ESPN under a soccer filter should not open its
   * remaining menus on football answers.
   */
  function chooseTemplate(key: string, seed: TemplateValues = {}) {
    const chosen = templates.data?.find((entry) => entry.key === key);
    /**
     * **The publisher is only dropped when the publisher actually changed**,
     * and before the league menu that distinction could not arise: this ran
     * from a `<select>` whose `onChange` cannot fire for the value it already
     * holds. `chooseLeague` calls it for every league, and PFF carries four of
     * them — so without this test, moving from PFF's FBS to its FCS signs the
     * operator out of PFF and makes them sign in again to the same publisher,
     * for a session that was still good. A `login_form` sign-in is a real
     * browser against a site with bot protection (ADR-0006); it is not a cost
     * to pay twice for no reason.
     *
     * A session earned from one publisher is still not a session at another,
     * which is what the branch keeps doing when the key does differ.
     */
    if (key !== templateKey) {
      setCredentialId(null);
      setSignIn({
        username: "",
        password: "",
        loginUrl: chosen?.credential_login_url ?? "",
      });
    }
    setTemplateKey(key);
    setValues(chosen ? templateDefaults(chosen, menuSport, seed) : {});
  }

  /**
   * Back to asking, with nothing carried over.
   *
   * Its own function because there are two ways to reach it — a league the menu
   * does not hold, and a sport filter that strands the one chosen — and the
   * credential is the part that is easy to leave behind. `provenCredentialId`
   * is read at Fetch and at Save without consulting the template, so a
   * `credentialId` surviving here would present a PFF session to whatever the
   * form was pointed at next.
   */
  function forgetTemplate() {
    setTemplateKey("");
    setValues({});
    setCredentialId(null);
    setSignIn({ username: "", password: "", loginUrl: "" });
  }

  /**
   * Choosing a league: the first question the screen asks, and the one that
   * settles the second.
   *
   * The publisher is **kept where it still applies**, which is the point of
   * having a Source control at all — somebody comparing PFF's Division II with
   * the NCAA template's switches between two leagues both carry, and
   * re-answering the publisher question at every switch would make the
   * comparison the thing this screen is worst at. `sourceFor` holds that rule,
   * and falls to the league's first publisher rather than to blank.
   */
  function chooseLeague(key: string) {
    setMenuLeague(key);
    const source = sourceFor(leagues.data, key, templateKey);
    if (source === null) {
      // A league the menu does not hold — a stale list, or the row being
      // cleared. Nothing to compose against, so the form goes back to asking,
      // and drops the credential with it.
      forgetTemplate();
      return;
    }
    chooseTemplate(source.template_key, source.selects);
  }

  /**
   * Choosing a publisher for a league already chosen.
   *
   * Separate from `chooseLeague` because the seed is the *other* publisher's:
   * PFF spells Division II `ncaa-d2` and the NCAA template spells it `d2`, and
   * carrying one across would open the second form on a value its own template
   * would refuse.
   */
  function chooseSource(key: string) {
    chooseTemplate(key, selectsFor(leagues.data, menuLeague, key));
  }

  /**
   * Narrowing by sport, and what it does to the league beside it.
   *
   * A league belongs to exactly one sport, so a filter that left one standing
   * would leave the Source control holding a template chosen for a league no
   * longer on screen, with a composition still running against it.
   * `chooseLeagueSport` drops it, and the form goes back to asking.
   */
  function narrowBySport(sport: string) {
    setMenuSport(sport);
    const kept = chooseLeagueSport(leagues.data ?? [], menuLeague, sport);
    if (kept !== menuLeague) chooseLeague(kept);
  }

  const [choices, setChoices] = useState<Record<string, FieldChoice>>({});
  /**
   * A position map made on this screen, before the profile exists.
   *
   * Null until somebody adds a Canonical Position Group, which is what keeps
   * the Source Template's composed map authoritative until it is deliberately
   * changed — and `merged` carries that map across rather than replacing it,
   * so adding a group never drops what the template declared.
   */
  const [positionMap, setPositionMap] = useState<Record<string, string> | null>(null);
  /**
   * Which targets published each path, and what each of them published.
   *
   * Filled by the scan, keyed by the Feed's **label** rather than its id,
   * because on a create the Feeds do not have ids yet — the label is what a
   * Binding can name and what the server resolves after `_apply_feeds`.
   */
  const [reportsByPath, setReportsByPath] = useState<Record<string, string[]>>({});
  const [samplesByPath, setSamplesByPath] = useState<Record<string, Record<string, unknown[]>>>(
    {},
  );

  /**
   * What each composed Feed is recorded as publishing, for the create request
   * (ADR-0061).
   *
   * The same measurement as `samplesByPath`, inverted: that one is keyed by
   * path because the review table reads it per field, and a Feed row stores it
   * per Feed. `publishedFieldsByFeed` owns the attribution across a
   * shape-deduped scan — paths carry to a Feed sharing a shape, samples never
   * do — because that rule is the one way this can quietly lie, and a rule
   * inside a component is a rule nothing can assert.
   */
  const publishedByFeed = useMemo(
    () =>
      publishedFieldsByFeed(
        (composed?.feeds ?? []).map((feed) => ({ label: feed.label, shape: feed.shape })),
        scanFeeds.map((feed) => ({
          label: feed.label,
          fields: Object.fromEntries(
            Object.entries(samplesByPath)
              .filter(([, byLabel]) => feed.label in byLabel)
              .map(([path, byLabel]) => [path, byLabel[feed.label] ?? []]),
          ),
        })),
      ),
    [composed, scanFeeds, samplesByPath],
  );
  /**
   * A field's per-report answers, where somebody has given it any.
   *
   * Beside `choices` rather than inside it, and the pair is the storage
   * model rather than a convenience: `choices[path]` is the one unscoped
   * Binding a field saves as by default, and an entry here means *every* one
   * of that field's reports materialises instead. Those are the only two
   * shapes the server accepts — one unscoped Binding beside one scoped one is
   * refused as `colliding_column` — so representing them as two maps makes
   * the illegal third state unspellable.
   *
   * A report with no entry still saves, carrying the field's own answer. See
   * `draftsForField`.
   */
  const [perReport, setPerReport] = useState<Record<string, Record<string, FieldChoice>>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Errors>({});

  // Where each rule's message is anchored, so a failed Save can bring the
  // offending control into view and put the cursor in it.
  const anchors: Record<AnchorKey, React.RefObject<HTMLElement>> = {
    name: useRef<HTMLElement>(null),
    league: useRef<HTMLElement>(null),
    identity: useRef<HTMLElement>(null),
    submit: useRef<HTMLElement>(null),
    fetch: useRef<HTMLElement>(null),
  };

  /**
   * The rows of the field table, by path, so a refusal about one can be
   * scrolled to.
   *
   * A ref map rather than state: these are DOM nodes, nothing renders from
   * them, and a `setState` per row as the table mounts would re-render the
   * screen six hundred times.
   */
  const rowAnchors = useRef(new Map<string, HTMLElement>());

  /**
   * What each row said when the last Save was sent.
   *
   * A refusal describes the review at the moment it was pressed, so a mark
   * taken from one has to stop being shown once its row is edited — and must
   * stay on every row that has not been. See `rowSignature`.
   */
  const submittedRows = useRef(new Map<string, string>());

  /**
   * Where to travel to once the errors have been painted.
   *
   * The move cannot happen inside the click handler: an error message that is
   * only rendered when it applies — the identity one — does not exist in the
   * DOM at the moment the rule fails, so scrolling there would silently do
   * nothing. Recording the intent and acting on it after the commit works for
   * anchors that are always present and ones that have just appeared alike.
   */
  const [travelTo, setTravelTo] = useState<Travel | null>(null);

  useEffect(() => {
    if (!travelTo) return;
    const target =
      travelTo.kind === "anchor"
        ? anchors[travelTo.key].current
        : rowAnchors.current.get(travelTo.path) ?? null;
    // The stylesheet already stands down for a reader who asked for less
    // motion; a smooth scroll would go around it.
    const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target?.scrollIntoView({ behavior: calm ? "auto" : "smooth", block: "center" });
    // Focus without a scroll of its own — the browser would otherwise snap the
    // element to the top edge and undo the centring.
    target?.focus({ preventScroll: true });
    setTravelTo(null);
    // `anchors` holds refs, which are stable for the life of the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelTo]);

  /** Drop a field's complaint as soon as it is being addressed. */
  function clearError(key: ErrorKey) {
    setErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  const identityPaths = useMemo(
    () => Object.entries(choices).filter(([, c]) => c.role === "identity").map(([path]) => path),
    [choices],
  );

  /**
   * Sign in, and keep the id of what that earned.
   *
   * Deliberately its own step rather than part of Save. The credential is what
   * makes the *next* step honest: discovery reads the fields this screen then
   * asks somebody to classify, and on a source that paywalls by column those
   * are different fields signed in than signed out. Saving first and adding
   * the credential afterwards — which is what this screen used to ask for —
   * means the review was done against the anonymous table, and nothing later
   * says so.
   */
  const authenticate = useMutation({
    mutationFn: () => {
      const kind = template?.credential_type === "bearer_token" ? "bearer_token" : "login_form";
      if (kind === "bearer_token") {
        if (!sourceUrl || !composedFeed) {
          throw new ApiError(
            422,
            "validation_failed",
            "The Feed has not finished composing yet. Wait a moment and try again.",
          );
        }
        // Prove against the same request the Run will make — a wrong token must
        // not unlock "Fetch and read fields". Sent whichever way the secret
        // arrives: a key read from the deployment's configuration is no more
        // proven than a pasted one, and a stale configured key fails here
        // rather than against an empty field table.
        const probe = {
          url: sourceUrl,
          method: sourceMethod,
          body: sourceBody,
          season,
        };
        // The deployment already holds this publisher's key, so the form has
        // not asked for one and has none to send. `from_template` says which
        // declaration to read it from; the key itself was never in the browser.
        if (usingConfiguredKey) {
          return api.createCredential({
            name: template?.publisher ?? "API token",
            credential_type: "bearer_token",
            from_template: templateKey,
            probe,
          });
        }
        return api.createCredential({
          name: template?.publisher ?? "API token",
          credential_type: "bearer_token",
          secret: { token: bearerToken.trim() },
          probe,
        });
      }
      return api.createCredential({
        name: template?.publisher ?? "Source sign-in",
        credential_type: "login_form",
        secret: {
          username: signIn.username.trim(),
          password: signIn.password,
          login_url: signIn.loginUrl.trim(),
        },
        // How this publisher's sign-in has to be performed, straight off the
        // template (ADR-0023). Composed rather than asked about: a subscriber
        // has no way of knowing that PFF's sign-in needs a real browser while
        // another publisher's is a JSON request, and it was a deployment-wide
        // setting until one deployment needed both answers at once. Null for a
        // template that has not declared one, which leaves the default.
        session_acquirer: template?.credential_acquirer ?? null,
      });
    },
    onSuccess: (credential) => setCredentialId(credential.id),
  });

  /**
   * Watching the sign-in the button started.
   *
   * The mutation resolving no longer means the sign-in worked — it means the
   * Credential Set was accepted and a worker has been told to go and sign in
   * (ADR-0033). The browser can legitimately take sixty to ninety seconds
   * against Wyscout, so what the panel renders comes from here rather than
   * from the mutation's result.
   *
   * Polling stops the moment the status settles, on the same terms the Run
   * screen already uses: a finished sign-in must not keep a timer alive for as
   * long as the tab is open.
   */
  /**
   * Whether this template's credential is a static token rather than a sign-in.
   *
   * The distinction is exactly what ADR-0033 is about, read from the other end.
   * A `login_form` is proved by a browser on a worker, which is why the request
   * cannot be held open and the outcome has to be polled. A bearer is proved by
   * one authenticated request *inside* the create call, so by the time this
   * screen holds its id the source has already accepted it. It therefore
   * carries no `sign_in_status` at all — null, the absence of the lifecycle
   * rather than a state in it — and there is nothing here to poll for.
   */
  const isBearer = template?.credential_type === "bearer_token";

  /**
   * Whether this deployment already holds the key, so the form asks for nothing.
   *
   * The server computed it, from the template's declared setting and that
   * setting's value, and sent a boolean — the key is not in the catalogue and
   * must not be, because a Credential Set is never returned to a browser.
   *
   * So the panel still has a button, and that is not ceremony: the token is
   * proved against the composed Feed before "Fetch and read fields" unlocks,
   * and a configured key can be as wrong as a pasted one — expired, revoked, or
   * a key for an account whose subscription lapsed. Pressing it is how that
   * becomes visible here instead of two steps later against an empty table.
   */
  const isPreconfigured = isBearer && template?.credential_preconfigured === true;

  /**
   * The operator chose to supply a key of their own, though the portal has one.
   *
   * A configured key is a default rather than a lock: one portal holds one
   * account's key, and wanting another is ordinary — a second subscription, a
   * key mid-rotation, or one profile that should read a different account from
   * the rest. The server already settles the precedence, since a posted secret
   * beats `from_template`; this is only which of the two the form is offering.
   *
   * Reset when the template changes, or switching from a publisher whose key is
   * configured to one whose is not would leave the form in override mode with
   * nothing to go back to.
   */
  const [overrideKey, setOverrideKey] = useState(false);
  useEffect(() => {
    setOverrideKey(false);
  }, [templateKey]);

  // What the form will actually send: the portal's key unless one was typed in
  // its place. Read by the mutation and by the hint beside Fetch, so the two
  // cannot describe different buttons.
  const usingConfiguredKey = isPreconfigured && !overrideKey;

  const credentialQuery = useQuery({
    queryKey: keys.credential(credentialId ?? ""),
    queryFn: () => api.readCredential(credentialId as string),
    enabled: credentialId !== null && !isBearer,
    refetchInterval: (query) =>
      signInSettled(query.state.data?.sign_in_status) ? false : POLL_RUN_MS,
  });

  const credential: CredentialSummary | null = credentialQuery.data ?? null;

  /**
   * Where the sign-in has got to, as this screen needs to branch on it.
   *
   * `signed-in` is deliberately not "a Credential Set exists". It used to be —
   * the endpoint could not answer until the sign-in had finished, so holding an
   * id meant holding a proven one. It no longer does, and reading it that way
   * would let somebody discover against a credential that turns out to be a
   * typo, review the anonymous field table, and be told nothing.
   */
  const signInState: "none" | "starting" | "running" | "failed" | "signed-in" =
    credentialId === null
      ? authenticate.isPending
        ? "starting"
        : "none"
      : isBearer
        ? // Proved by the create request itself, and refused there if it was
          // wrong — so holding the id *is* holding a proven one, which is what
          // holding an id meant for every credential before ADR-0033.
          "signed-in"
        : // A poll that has given up is `failed` rather than `running`. It is
          // not the sign-in's own verdict, but "spin forever" is the one outcome
          // this screen must never reach, and the panel offers a way out of
          // `failed` where it offers none out of `running`.
          credentialQuery.isError
          ? "failed"
          : credential === null
            ? "running"
            : credential.sign_in_status === "succeeded"
              ? "signed-in"
              : credential.sign_in_status === "failed"
                ? "failed"
                : "running";

  /** Sign in again as somebody else. The old Credential Set is simply left. */
  function forgetSignIn() {
    setCredentialId(null);
    setBearerToken("");
    setSignIn((current) => ({ ...current, password: "" }));
    authenticate.reset();
  }

  /** Try again after a refusal.
   *
   *  A fresh Credential Set rather than a retry of the old one — the refused
   *  record is what carried the reason, and it has now been read. Deleting it
   *  on the way out is the other half of that: the row exists so the reason can
   *  be shown, and left behind it is an unusable Credential Set holding an
   *  encrypted password, one per mistyped attempt, in a list the whole
   *  Workspace can see (ADR-0016).
   *
   *  Best-effort. If the delete fails there is nothing useful to say about it
   *  here — the operator asked to type their password again, and refusing to
   *  let them because a tidy-up did not land would be a worse screen. */
  function signInAgain() {
    const refused = credentialId;
    setCredentialId(null);
    authenticate.reset();
    if (refused) void api.discardCredential(refused).catch(() => undefined);
  }

  // A template that says it signs in is not ready to be read until the sign-in
  // has **succeeded**. Asynchronous means the request is not held open; it does
  // not mean the operator carries on regardless (ADR-0033).
  const needsSignIn = Boolean(template?.needs_credential) && signInState !== "signed-in";

  /** The Credential Set to present, which is only ever a proven one.
   *
   *  Holding an id stopped meaning holding a proven one when the sign-in moved
   *  off the create request, and every consumer of it downstream — discovery,
   *  and the profile that adopts it — wants the proven reading. */
  const provenCredentialId = signInState === "signed-in" ? credentialId : null;

  // Which Feed the template composed, and how it is requested. The first one
  // stands for the source when the credential is proved (ADR-0034 for POST
  // Feeds). Each scanned Feed carries its own method and body.
  const composedFeed = composed?.feeds[0];
  const sourceMethod = composedFeed?.method ?? "GET";
  const sourceBody = composedFeed?.body ?? null;

  /**
   * How far through the field scan we are, or null when it is not running.
   *
   * Held here rather than derived from the mutation because a mutation is
   * pending or not and this scan is seven requests: "reading Key Passing, 5 of
   * 7" is the difference between a wait somebody sits through and a wait that
   * reads as a hang. `label` is the Feed's own, so the list a person watches is
   * the list of names the template offered them.
   */
  /**
   * Flipped when this screen goes away, so a read in flight stops being polled.
   *
   * **It abandons the poll, not the read.** The server finishes what it
   * started, stores it, and joins the next submission of the same read to it —
   * so coming back and pressing the button again re-attaches rather than
   * paying for the address twice. What this stops is the twenty minutes of
   * requests, and the state writes into a component that no longer exists.
   */
  const gone = useRef({ aborted: false });
  useEffect(() => {
    // **Cleared on mount as well as set on unmount**, which is not symmetry
    // for its own sake: `main.tsx` renders under `React.StrictMode`, so in
    // development every component is mounted, unmounted and mounted again
    // while the *same* ref object survives. Setting the flag only in the
    // cleanup would leave it true for the real mount that follows, and every
    // scan would abandon itself on its first poll — in development only,
    // which is the worst place for it to hide.
    gone.current.aborted = false;
    return () => {
      gone.current.aborted = true;
    };
  }, []);

  const [scan, setScan] = useState<{
    done: number;
    total: number;
    label: string;
    /** Which attempt at this address is in flight; 1 unless it is being retried. */
    attempt: number;
    /** How long this address has been reading, in milliseconds.
     *
     * The read is polled rather than awaited, so this is what separates "still
     * reading" from "hung" on an address that legitimately takes minutes —
     * `defense/pass_rush` was measured at about two. Without it the screen is
     * as silent as the one that made a reviewer press the button again. */
    elapsedMs: number;
  } | null>(null);

  /**
   * Reading the source, and the dictionary its statistics resolve against.
   *
   * A query rather than part of the discover mutation, which is where it used
   * to live. Fetching it there made re-running discovery the only way to
   * refresh the dropdown — and `discover.onSuccess` calls `setChoices`, so
   * adding one statistic would have thrown away every role and statistic
   * already set. On its own key it refreshes on invalidation and the table
   * underneath it does not move.
   *
   * Filtered to the sport, which the suggestion engine already does and says
   * why: without it a soccer field called `penalties` binds to football's
   * entry and inherits `lower_is_better`, ranking every player backwards
   * (ADR-0017).
   */
  const statsQuery = useQuery({
    queryKey: keys.statsFor(sport),
    queryFn: () => api.listStats(sport),
  });

  /** Which field's "+ New" form is open. One at a time: two open forms are two
   *  half-typed statistics and no way to tell which row either belongs to. */
  const [newStatFor, setNewStatFor] = useState<string | null>(null);

  /** Reading the source. */
  const discover = useMutation({
    mutationFn: async (overrides?: { scope?: string; recordPath?: string }) => {
      const chosenScope = overrides?.scope ?? scope;
      // A composed source declares where its players are, so discovery reads
      // the right array on the first fetch. ESPN needs it: every response ships
      // a sixty-entry glossary of stat abbreviations beside as few as
      // thirty-eight athletes, so the largest array is the wrong one.
      const chosenRecordPath =
        overrides?.recordPath ?? recordPath ?? composed?.defaults.record_path ?? "";
      // One read per shape, in sequence. Sequential rather than concurrent
      // because the publisher is the constraint and seven parallel reads of a
      // paid seat is the sort of thing that gets a token revoked — and because
      // a bar that advances one named step at a time is the thing being asked
      // for here.
      //
      // A pasted URL has no composition, so it scans exactly the one address
      // typed: `scanFeeds` is empty and this falls back to it.
      const targets: {
        url: string;
        label: string;
        method: "GET" | "POST";
        body: unknown;
        // How this address's coded statistics are decoded, where the template
        // composed one. Carried through the scan because there is no profile
        // yet to read it from — and without it an InStat address is reviewed
        // as twelve identity and timing fields with no statistic among them.
        decoding?: Decoding;
        // And the address each of this Feed's records expands into, carried
        // for the same reason and against a sharper version of the same
        // defect: ESPN soccer's roster publishes identity, bio and position
        // and *no statistics at all*, so an address read without this recipe
        // is reviewed as forty-three biographical fields — and a Binding
        // cannot be made against a path discovery never named.
        expansion?: Expansion;
      }[] =
        scanFeeds.length > 0
          ? scanFeeds.map((feed) => ({
              url: feed.url,
              label: feed.label,
              method: feed.method ?? "GET",
              body: feed.body ?? null,
              ...(feed.decoding && "catalogue" in feed.decoding
                ? { decoding: feed.decoding as Decoding }
                : {}),
              ...(feed.expansion && "url_path" in feed.expansion
                ? { expansion: feed.expansion as Expansion }
                : {}),
            }))
          : [{ url: sourceUrl, label: sourceUrl, method: sourceMethod, body: sourceBody }];

      // Merged on `path`, first read wins — for the *suggestion*. Every
      // target returns the same players, so a field two shapes both publish is
      // one field and the two agree about it; measured, not assumed, for
      // Wyscout, which is what `test_no_two_filters_disagree_about_a_shared_column`
      // pins.
      //
      // **That measurement is Wyscout's and it is false for PFF**, where
      // `interceptions` is thrown in the passing report and caught in
      // coverage. So which targets published a path, and what each of them
      // published, is kept beside the merge rather than thrown away with it:
      // one merged row is still the right default, and the reviewer can now
      // see what the default is hiding.
      const merged = new Map<string, DiscoveredField>();
      const seenIn = new Map<string, string[]>();
      const samplesByPath = new Map<string, Record<string, unknown[]>>();
      let firstResult: Awaited<ReturnType<typeof api.discover>> | null = null;
      /**
       * Where each address answered, and how much it answered with.
       *
       * The merge below keeps the first address's result and replaces only its
       * `fields`, so the other twelve answers to "where are the players?" were
       * read and thrown away. On PFF they are the interesting ones: each report
       * publishes under its own key, so the screen asked which single list held
       * the players and offered `passing_summary` — one report's — preselected,
       * beside a count that read as the whole division's.
       *
       * Shaped as `DiscoveredFeed` so this and the saved-profile route describe
       * a source the same way. The ids are empty because the Feeds do not exist
       * yet; nothing reads them, and `feed_label` is already how a Binding names
       * a Feed before a profile is saved.
       */
      const perAddress: DiscoveredFeed[] = [];

      for (const [index, target] of targets.entries()) {
        setScan({ done: index, total: targets.length, label: target.label, attempt: 1, elapsedMs: 0 });
        // **Which attempt is in flight, held outside the state setter.**
        // `onRetry` and `onPoll` both write `scan`, and they interleave: a
        // retry sets "attempt 2 of 3" and the first poll of that attempt
        // lands a few hundred milliseconds later. Reading the attempt back
        // from a literal in `onPoll` erased the notice the reviewer had just
        // been shown, and made the `scan.attempt === 1` guard below dead.
        let liveAttempt = 1;
        let read;
        try {
          // Asked again while the failure looks like an accident — a dropped
          // connection, a gateway that gave up, a source still unreachable
          // after the Fetcher spent its own three attempts. `retry.ts` holds
          // which failures those are, and refuses the ones a human has to fix.
          //
          // Per address rather than around the whole scan: the twelve that
          // already answered are not re-read because the thirteenth dropped,
          // which is what made this worth pressing a button over.
          read = await withRetry(
            () =>
              // **Started and polled rather than awaited in one request.**
              // A read of this address can legitimately take minutes — PFF's
              // `defense/pass_rush` answered 504 after 30 to 60 seconds and
              // 200 only on a later attempt, about two minutes of retrying for
              // one success — and a Netlify proxy rewrite times out at
              // twenty-six seconds and cannot be configured otherwise. Held
              // open, the edge answered the browser while the read was still
              // going, `withRetry` below started a second read on top of it,
              // and the reviewer was shown "Something went wrong." with no
              // code, because the response had never come from the API.
              //
              // `withRetry` is still in front of this, and is now safe to be:
              // a resubmitted read joins the one already running instead of
              // starting another.
              awaitDiscoveryJob<DiscoveryResult>(
                () =>
                  api.startDiscoveryJob({
                    url: target.url,
                    // A POST Feed is read by sending the document the template
                    // composed. There is no profile yet to carry it, so it travels
                    // with the request.
                    method: target.method,
                    body: target.body as Record<string, unknown> | unknown[] | null,
                    // Same reason the body travels here: there is no profile yet to
                    // carry it, and a coded Feed read without its decoding shows a
                    // reviewer a payload nobody could bind.
                    ...(target.decoding ? { decoding: target.decoding } : {}),
                    // Same reason the decoding travels here: without a profile
                    // there is nothing to read the recipe from, and a roster
                    // read without it has no statistics to review.
                    ...(target.expansion ? { expansion: target.expansion } : {}),
                    scope: chosenScope || null,
                    record_path: chosenRecordPath || null,
                    // A composed address carries `{season}` rather than a literal
                    // year, and there is no profile yet to read the season from — it
                    // is the template parameter on this form. Without it the request
                    // goes out asking for a season called "{season}".
                    season,
                    // Signed in where the template says the source needs it. This is
                    // the difference between a field table with grades in it and one
                    // without, and the anonymous one is not an error — it is a
                    // complete response with the paid-for columns missing.
                    //
                    // Only a Credential Set whose sign-in succeeded. The button that
                    // reaches here is disabled otherwise, so this is belt to that
                    // brace — but the server refuses an unproven one with a 409
                    // (ADR-0033), and meeting that as a red banner on the field table
                    // would be a worse explanation than the one the sign-in panel is
                    // already giving.
                    credential_set_id: provenCredentialId,
                    // Set before any profile exists, which is the point: the first
                    // request this screen makes is the one that meets the refusal.
                    user_agent: userAgent || composed?.defaults.user_agent || null,
                    // Same reason season travels here: there is no profile yet, so the
                    // sport that scopes the Stat Dictionary is the form value (or the
                    // template default). Without it, discovery indexes every sport.
                    sport: sport || composed?.defaults.sport || null,
                  }),
                (jobId) => api.readDiscoveryJob(jobId),
                {
                  fail: (status, code, message, field, details) =>
                    new ApiError(status, code, message, field, details),
                  // The screen counts up while one address is being read, so a
                  // two-minute wait reads as progress rather than as a hang.
                  signal: gone.current,
                  onPoll: (elapsedMs) =>
                    setScan({
                      done: index,
                      total: targets.length,
                      label: target.label,
                      attempt: liveAttempt,
                      elapsedMs,
                    }),
                },
              ),
            {
              onRetry: (attempt) => {
                liveAttempt = attempt;
                setScan({
                  done: index,
                  total: targets.length,
                  label: target.label,
                  attempt,
                  elapsedMs: 0,
                });
              },
            },
          );
        } catch (error) {
          setScan(null);
          // Named, and the whole scan fails. Keeping the shapes that did
          // answer would publish a short field list that looks complete —
          // which is the defect this scan exists to end, reintroduced as an
          // error path.
          if (error instanceof ApiError) {
            throw new ApiError(
              error.status,
              error.code,
              `${target.label}: ${error.message}`,
              error.field,
              error.details,
            );
          }
          throw error;
        }
        if (!firstResult) firstResult = read;
        perAddress.push({
          id: "",
          label: target.label,
          url: target.url,
          array_path: read.array_path,
          record_count: read.record_count,
        });
        for (const field of read.fields) {
          const held = merged.get(field.path);
          if (!held) {
            merged.set(field.path, field);
          } else if (field.values.length > 0) {
            // **The vocabulary is unioned; everything else stays the first
            // Feed's.** A partitioning publisher splits its players between
            // addresses — ESPN fetches Offense, Defense, Scoring and Special
            // Teams separately — so each one sees only its own slice of the
            // position labels. First-write-wins kept Offense's `QB` and threw
            // away `LB`, `RB` and `PK`, which left the add-a-group dialog
            // offering a quarter of the vocabulary with nothing saying so.
            //
            // Only the values: the suggested role, the samples and the counts
            // are per-Feed facts the table already reports per Feed, and
            // merging those would be inventing a reading nobody took.
            merged.set(field.path, {
              ...held,
              values: unionedLabels([held, field]),
            });
          }
          // Keyed by the target's label because that is what a Binding can
          // name on a create — the Feeds have no ids until the profile is
          // saved, and `feed_label` is how the server resolves one afterwards.
          const labels = seenIn.get(field.path) ?? [];
          if (!labels.includes(target.label)) labels.push(target.label);
          seenIn.set(field.path, labels);
          const samples = samplesByPath.get(field.path) ?? {};
          samples[target.label] = field.samples;
          samplesByPath.set(field.path, samples);
        }
      }
      setScan(null);

      const result = {
        ...firstResult!,
        // Sorted, so the table does not reorder itself because a different
        // shape answered first.
        fields: [...merged.values()].sort((a, b) => a.path.localeCompare(b.path)),
        // Every address, not the first — which is what the saved-profile route
        // has always returned, so both now answer "what did this source say"
        // the same way.
        feeds: perAddress,
        // Summed for the same reason. "560 records" under a thirteen-report
        // source is the first report's total wearing the whole source's label.
        record_count: perAddress.reduce((total, one) => total + one.record_count, 0),
      };
      return {
        result,
        chosenScope,
        reports: Object.fromEntries(seenIn),
        samples: Object.fromEntries(samplesByPath),
      };
    },
    onSuccess: ({ result, chosenScope, reports, samples }) => {
      // Only a scan that read every group describes every group, so the
      // snapshot behind the Stat group popup is taken from this read alone.
      if (!chosenScope) setGroupFields({ url: result.url, fields: result.fields });
      setReportsByPath(reports);
      setSamplesByPath(samples);
      // A fresh scan replaces the evidence, so any per-report decision taken
      // against the old one is no longer about reports anybody has seen.
      setPerReport({});
      setExpanded(new Set());
      const suggested: Record<string, FieldChoice> = Object.fromEntries(
        result.fields.map((field) => [
          field.path,
          {
            role: field.suggested_role,
            statKey: field.suggested_stat_key,
            // What the Stat Dictionary entry declares, the way the Direction is
            // inherited — null where it declares nothing, which leaves the box
            // blank rather than showing a 1 nobody chose. A Source Template's
            // composed defaults still override this below, which is the right
            // precedence: the template reviewed the whole mapping.
            weight: field.suggested_weight ?? null,
            // Undeclared, because discovery has nothing to suggest: where a
            // column belongs in the header is a judgement about one file, so
            // there is no per-statistic answer to inherit the way the weight is
            // inherited from the dictionary entry.
            sortOrder: null,
            parseRule: {},
          },
        ]),
      );
      // A template may compose its whole field review, not just its addresses.
      // MLS NEXT Pro does: its metric mapping is the one in the reference tree
      // somebody actually went through, and it carries per-metric weights, the
      // parse rule that reads `HH:MM:SS` as minutes and the one that joins a
      // name out of two fields. None of that is recoverable from the payload,
      // so discovery cannot suggest it and it arrives declared.
      //
      // The template's answer wins over discovery's guess where both have one,
      // and only for paths the payload actually published — a declaration that
      // does not appear is a template gone stale, and binding an absent path
      // would hide that behind a profile that saves and then fails to run.
      for (const binding of composed?.defaults.bindings ?? []) {
        if (!suggested[binding.path]) continue;
        suggested[binding.path] = {
          role: binding.role,
          statKey: binding.stat_key ?? null,
          weight: binding.weight ?? null,
          // Composed where the template declares one, on the same terms as the
          // weight: a curated mapping that chose an order has reviewed the
          // whole header, and rebuilding it from role and statistic alone
          // would drop that silently.
          sortOrder: binding.sort_order ?? null,
          parseRule: binding.parse_rule ?? {},
        };
      }
      // A template already knows what tells its players apart — `player_id` for
      // PFF, `athlete.id` for ESPN — so the paths it names arrive marked as the
      // Identity Key rather than waiting to be found in a table of six hundred
      // fields. Only paths the payload actually published: a declaration that
      // does not appear is a template gone stale, and marking an absent path
      // would hide that behind a profile that saves and then fails to run.
      for (const path of composed?.defaults.identity_key ?? []) {
        if (suggested[path]) suggested[path] = { ...suggested[path], role: "identity" };
      }
      setChoices(suggested);
      // Only the pasted-URL route needs a name from here: a composed one is
      // already following its parameters in the effect below, and has been
      // since before the fetch. The hostname is the right answer for a URL
      // somebody typed and the wrong one for a template — thirteen PFF reports
      // are all `premium.pff.com`.
      if (!nameIsTyped && !composed) setName(new URL(sourceUrl).hostname);
    },
    onError: () => {
      setScan(null);
      // Same reason Save travels to its refusal: the banner at the top of a
      // long form is, from the Fetch button, no message at all.
      setTravelTo({ kind: "anchor", key: "fetch" });
    },
  });

  const runDiscovery = (overrides?: { scope?: string; recordPath?: string }) =>
    discover.mutate(overrides);

  const chooseGroup = (group: string) => {
    setScope(group);
    void runDiscovery({ scope: group });
  };

  /**
   * Prefill what the publisher already knows the answer to — once per answer.
   *
   * Keyed on the answer itself rather than on the whole composition, because
   * the composition changes every time a parameter moves: prefilling on that
   * would reset a league somebody had corrected the moment they changed the
   * season, which is a form quietly discarding an edit.
   *
   * The key is the template *plus the sport and league it composed*, and the
   * two extra parts are load-bearing: a template key alone was one key for
   * both of ESPN's surfaces once they became a single menu entry. The default
   * league is NFL, so the first composition prefilled `football` and claimed
   * the key, and picking any of the six soccer leagues afterwards was skipped
   * — leaving a soccer profile on the football Stat Dictionary, which the
   * server refuses. Changing the season still leaves the key alone, so a
   * corrected league survives exactly as before.
   */
  const prefilled = useRef<string | null>(null);

  useEffect(() => {
    if (!composed) return;
    const answer = [
      composed.template_key,
      composed.defaults.sport ?? "",
      composed.defaults.league ?? "",
    ].join("\u0000");
    if (prefilled.current === answer) return;
    prefilled.current = answer;
    const defaults = composed.defaults;
    if (defaults.league !== undefined) setLeague(String(defaults.league));
    if (defaults.sport !== undefined) setSport(String(defaults.sport));
    // Cleared, not left standing, when a template declares no Record Path.
    // Choosing ESPN and then switching to PFF would otherwise carry `athletes`
    // across to a publisher that has no such array, and the Run would fail as
    // `no_players` against a profile whose form looked untouched.
    setRecordPath(defaults.record_path ?? "");
    // Cleared on the same terms, and for the same reason: a template that
    // declares nothing is declaring `fail`, so switching from a publisher
    // measured repeating rows to one that has not been would otherwise carry
    // `merge` across and fold collisions nobody has looked at.
    setRepeatedIdentity(repeatedIdentityFor(defaults));
  }, [composed]);

  /**
   * The season is the exception, and follows every recompose.
   *
   * It is a template parameter *and* the number in the published filename, and
   * the two disagreeing is not a stale prefill but a wrong file: 2025 addresses
   * publishing `..._2024_raw.csv`. So this one is not the form's to keep.
   */
  const composedSeason = composed?.defaults.season;

  useEffect(() => {
    if (composedSeason !== undefined) setSeason(Number(composedSeason));
  }, [composedSeason]);

  /**
   * The name follows the parameters, until somebody types one.
   *
   * **Not keyed like the League prefill above, and the difference is the
   * point.** That one fires once per answer so a corrected League survives the
   * next recompose; a League is a free-text field somebody is expected to
   * correct. The name is a *description of the choices made* — "PFF — Player
   * Statistics (FBS, Reg + Post)" is the division and the week filter read
   * back — so while nobody has written their own, every recompose owes it a
   * new one. Suggesting it once was how a source came to be called FBS in the
   * `fcs` League.
   *
   * `composed.parameters` rather than the form's `values`: the server resolves
   * implications and fills in what was not sent, so this names the choices
   * about to be saved rather than the ones on screen.
   */
  useEffect(() => {
    if (nameIsTyped || !template || !composed) return;
    setName(
      composedSourceName(
        template.name,
        applicableParameters(template, composed.parameters),
        composed.parameters,
      ),
    );
  }, [nameIsTyped, template, composed]);

  /**
   * The Sport control, defined once and placed by each route.
   *
   * **Before the fetch, and above the composed addresses.** It began in step 2
   * with the other Source Profile fields, which is where it belongs by *kind*
   * and was the wrong place by *time*: step 2 does not render until a field
   * scan has returned, so the sport could not be seen when the source was
   * chosen and could not be answered before it was used.
   *
   * And it is used. `runDiscovery` scopes the field scan's statistic
   * suggestions by sport, so on the pasted-URL route every scan ran as football
   * — the state's initial value — with no control on screen to say otherwise. A
   * template prefilled its own sport in time to be right; a pasted URL never
   * could.
   *
   * Placed rather than rendered in one shared spot because the two routes put
   * it in different company. On the template route it belongs with the settings
   * that *decide* it, above the addresses those settings composed — a hundred
   * and fifty-four URLs between a choice and its consequence is a scroll nobody
   * should have to make to check one word. On the URL route there are no
   * composed addresses, so it follows the ones that were typed.
   *
   * Null while nothing has composed, which is the template route before its
   * first composition returns: see `sportsOffered`.
   */
  const sportField =
    offeredSports.length === 0 ? null : (
      <Field
        label="Sport"
        hint={
          route === "template"
            ? `The folder published files land in, and it scopes the statistics offered below. These settings fetch ${offeredSports.join(" and ")}, so there is nothing to choose. Gender belongs in the league name.`
            : "The folder published files land in, and it scopes the statistics offered below. Gender belongs in the league name."
        }
      >
        {/* Left *enabled* with a single option, where that might have argued
            for either. `PaginationChoice` sets the precedent for hiding — "a
            control that cannot apply still reads as a setting somebody has to
            consider" — and this is the other case: the Sport does apply, and is
            required, and its answer is simply already known. A required field
            nobody can see is worse than one with a single option, and a
            disabled one invites the question of what is wrong with it. */}
        <Select value={sport} onChange={(e) => setSport(e.target.value)}>
          {offeredSports.map((option) => (
            <option key={option} value={option}>
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </option>
          ))}
        </Select>
      </Field>
    );

  /** The rules that are currently broken, in screen order. */
  function validate(): Errors {
    const satisfied: Record<ErrorKey, boolean> = {
      name: name.trim() !== "",
      league: league.trim() !== "",
      identity: identityCount(choices) > 0,
    };
    const found: Errors = {};
    for (const rule of REQUIRED) {
      if (!satisfied[rule.key]) found[rule.key] = rule.message;
    }
    return found;
  }

  const create = useMutation({
    mutationFn: async () => {
      const body: SourceProfileInput = {
        // What the publisher knows first, so that what the form knows wins over
        // it — a prefilled league somebody corrected stays corrected.
        ...(composed?.defaults ?? {}),
        name,
        league,
        sport,
        season,
        identity_key: identityPaths.length > 0 ? identityPaths : deriveIdentity(choices),
        // After the spread, so the control wins over the template that seeded
        // it — which is the point of offering it here at all. A reviewer who
        // never touches it sends back exactly what the template declared.
        repeated_identity: repeatedIdentity,
        // A position map made on this screen, where one was. It travels with
        // the field review in the same request for the same reason that does:
        // there is one profile and one transaction, and a map written
        // afterwards would be a second request that can fail on its own.
        ...(positionMap ? { position_map: positionMap } : {}),
        // Carried across whole rather than rebuilt field by field. Rebuilding
        // is what silently dropped `conference` — composed by the only thing
        // that can know which competition a Feed named, and the thing the
        // per-conference counts are made of — and a Feed property the next
        // template composes would be dropped the same way.
        feeds: composed
          ? composed.feeds.map((feed) => ({
              ...feedInputFrom(feed),
              // What the scan saw this address publishing (ADR-0061). Carried
              // onto the Feed so the profile's own Edit Fields screen offers
              // its per-report disclosure from birth, instead of asking for a
              // serial re-read of every report to learn what this scan has
              // just finished measuring.
              //
              // Only where there is something to record, and **non-empty**
              // rather than merely present: `{}` is truthy in JS, so a truthy
              // check would send an empty reading for every Feed on a create
              // that happens before any scan — which the server stores with no
              // timestamp and so reads as "nobody has looked", the state it
              // already was. The missing key means "leave the stored reading
              // alone", which is what the pasted-URL route and a Feed no shape
              // of the scan covered both want.
              ...(Object.keys(publishedByFeed[feed.label] ?? {}).length > 0
                ? { published_fields: publishedByFeed[feed.label] }
                : {}),
            }))
          : [{ url, pagination }],
        // The field review travels **with** the profile, in one request.
        //
        // It used to be a second PUT after the create returned, and that could
        // not be made safe: a refused `stat_key` answered 422 with the profile
        // already saved, so pressing Save again met "that league and season is
        // already published" — naming the source the first press had left
        // behind, and offering no way to connect the two. One request is one
        // transaction, and a refusal now leaves nothing behind to collide
        // with.
        // One Binding per field, or one per report where somebody asked for
        // that. A field nobody overrode emits exactly what this screen has
        // always emitted; `draftsForField` owns both shapes and the rule that
        // an override materialises all of a field's reports rather than one.
        bindings: Object.entries(choices).flatMap(([path, choice]) =>
          draftsForField(path, choice, reportsByPath[path] ?? [], perReport[path]),
        ),
        scope: scope || null,
        record_path: recordPath || null,
        // The template's identity where it sets one (ESPN does), otherwise
        // whatever was typed on the URL route, otherwise the honest default.
        user_agent: userAgent || composed?.defaults.user_agent || null,
        // Recorded so the parameters can be changed later and every Feed
        // recomposed. Null for a pasted URL.
        template_key: composed?.template_key ?? null,
        template_parameters: composed?.parameters ?? {},
        // The profile adopts the Credential Set that was used to read its
        // fields, so the Run presents the same login the review was done
        // against — and only if that sign-in succeeded, which is the same
        // condition discovery was allowed under.
        credential_set_id: provenCredentialId,
      };
      return await api.createProfile(body);
    },
    onSuccess: async (profile) => {
      // The dashboard's list is now one source short of the truth.
      await queryClient.invalidateQueries({ queryKey: keys.profiles });
      navigate(`/sources/${profile.id}`);
    },
  });

  /**
   * Pressing Save always does something — either it saves, or it says which
   * field is at fault and takes you to it.
   *
   * Three outcomes now rather than two, and the new one is the point of this
   * change: a review the server would refuse is refused **here**, at the row
   * that causes it, without a request. Those rules are the server's own
   * (`binding-problems.ts` says how they stay that way), so this is the same
   * refusal arriving before the press instead of after it.
   */
  function save() {
    const found = validate();
    setErrors(found);

    const firstBroken = REQUIRED.find((rule) => found[rule.key]);
    if (firstBroken) {
      setTravelTo({ kind: "anchor", key: firstBroken.key });
      return;
    }
    const blocked = firstMarkedRow(liveProblems);
    if (blocked) {
      setTravelTo({ kind: "row", path: blocked });
      return;
    }
    submittedRows.current = new Map(reviewRows.map((row) => [row.key, rowSignature(row)]));
    create.mutate();
  }

  const discovery = discover.data?.result ?? null;
  const stats = statsQuery.data ?? [];
  const busy = discover.isPending || create.isPending;
  // A refused parameter is shown against the control that caused it, so only a
  // rejection with no parameter to blame — an unknown template — reaches the
  // banner at the top of the screen.
  const composeError = composition.error instanceof ApiError ? composition.error : null;
  // Fetch refusals are shown next to the Fetch button (same reason Save's are
  // shown next to Save): a top-of-page banner on a long form reads as silence.
  const discoverError = asApiError(discover.error);
  const error =
    asApiError(authenticate.error) ??
    (composeError && !composeError.field ? composeError : null);
  // Kept out of the banner above: a refused Save is an answer to a button at
  // the bottom of the screen, and it is shown there instead.
  const saveError = asApiError(create.error);

  const grouped = useMemo(() => groupByBlock(discovery?.fields ?? []), [discovery]);
  const unknownCount = Object.values(choices).filter((c) => c.role === "ignored").length;

  /**
   * The review as the rules read it: one row per Binding this Save would send.
   *
   * Built from `discovery.fields` rather than from `choices`, although the
   * answers come from `choices` — the fields are what is on screen, and a
   * problem against a path with no row to sit under could not be shown.
   *
   * **One row per field until a field is read apart, and one per Feed after**,
   * which is `problemRowsForField`'s whole point: a split field sends no
   * unscoped Binding, so judging its own answer marked a row whose controls are
   * deliberately hidden — *Statistic with no statistic chosen* against a field
   * whose every Feed has one, blocking a Save the server would have accepted.
   * The per-Feed rows are judged instead, where the statistic actually is.
   *
   * An unsplit row is unscoped, and stays so. A profile saved from this screen
   * has no Feeds yet, so there is nothing to scope that Binding *to*; the
   * fan-out qualifies each column per Feed afterwards (ADR-0055), which is why
   * `feedIds` is empty below and the collision rule falls back to the one
   * column space they all share.
   */
  /**
   * The unsaved source, as the add-a-group dialog needs it.
   *
   * **The labels come from the field somebody set to Position**, which is the
   * only field whose values are a position vocabulary — and discovery counts
   * the distinct values of a low-cardinality text field precisely so this
   * screen has them. A Run would have recorded them, and this source has never
   * had one.
   *
   * Null where no field is bound as Position: there is nothing to group, and
   * the dialog says so rather than offering an empty list.
   */
  /**
   * What feeds each of this sport's Canonical Position Groups, for the detail
   * panel under the Statistic menu.
   *
   * Fetched once here and handed to every cell rather than queried per row:
   * one cache key either way, but a review of six hundred fields would
   * otherwise hold six hundred subscriptions to it.
   */
  const { data: offeredGroups } = useQuery({
    queryKey: keys.positionGroups,
    queryFn: api.listPositionGroups,
  });
  const groupLabels = useMemo(
    () => labelsFor(offeredGroups, sport),
    [offeredGroups, sport],
  );

  const positionDraft = useMemo(() => {
    const path = Object.entries(choices).find(([, choice]) => choice.role === "position")?.[0];
    // **Every Feed that publishes the path, not the first.** Discovery reports
    // one row per Feed per path, and a partitioning publisher splits its
    // players between them — ESPN sends Offense, Defense, Scoring and Special
    // Teams, so the first row's labels are a quarter of the vocabulary and
    // nothing on the screen would have said so.
    const perFeed = (discovery?.fields ?? []).filter(
      (candidate) => candidate.path === path,
    );
    return {
      labels: unionedLabels(perFeed),
      positionMap: positionMap ?? composed?.defaults.position_map ?? {},
      onMapped: setPositionMap,
    };
  }, [choices, discovery, positionMap, composed]);

  const reviewRows = useMemo<ProblemRow[]>(
    () =>
      (discovery?.fields ?? []).flatMap((field) =>
        problemRowsForField(
          field.path,
          choices[field.path] ?? {
            role: "ignored" as Role,
            statKey: null,
            weight: null,
            sortOrder: null,
            parseRule: {},
          },
          reportsByPath[field.path] ?? [],
          perReport[field.path],
        ),
      ),
    [discovery, choices, reportsByPath, perReport],
  );

  /**
   * What this review would be refused for, as it is being made.
   *
   * The dictionary is already scoped to the sport — `statsQuery` asks for one
   * sport's entries — which is what makes an unknown key here the same claim
   * the server makes (ADR-0017).
   */
  const liveProblems = useMemo(
    () =>
      bindingProblems(reviewRows, {
        knownStatKeys: stats.map((entry) => entry.key),
        feedIds: [],
        sport,
      }),
    [reviewRows, stats, sport],
  );

  /**
   * A refusal that arrived anyway, put against the rows it names.
   *
   * There are two ways to meet one even with the rules running locally: a
   * dictionary edited in another tab, and every 422 these rules do not model —
   * a league and season already published, a weight out of bounds. The second
   * kind has no row to blame and stays in the banner.
   */
  const serverProblems = useMemo(
    () => (saveError ? problemsFromRefusal(saveError, reviewRows, submittedRows.current) : []),
    [saveError, reviewRows],
  );

  /**
   * The message to show under each row, live problems first.
   *
   * Live first because they describe the rows as they *are*: a refusal is a
   * statement about the review at the moment Save was pressed, and leaving it
   * on top of a row since corrected would be the screen contradicting itself.
   */
  const problemByPath = useMemo(
    () => markedRows(liveProblems, serverProblems),
    [liveProblems, serverProblems],
  );

  /**
   * A refused Save brings its own refusal into view.
   *
   * The screen is long — the field table between the League box and the Save
   * button runs to dozens of rows — so a message painted at the top of it is,
   * from where the reader is standing, no message at all: the button was
   * pressed, nothing visibly happened, and the source appears not to have
   * saved for no stated reason. Keyed on `isError` rather than on the error
   * itself so that pressing Save twice against the same refusal travels twice;
   * `mutate` clears the flag on the way out and the failure sets it again.
   *
   * **Where it travels is now the refusal's own answer.** A problem naming a
   * path goes to that row; one naming a control the form owns goes to the
   * control, and sets its inline message, so the refusal is readable where the
   * correction gets made. Only a refusal about neither — an unknown template,
   * a league and season already published — still lands on the banner beside
   * the button.
   */
  useEffect(() => {
    if (!create.isError) return;
    const refused = firstMarkedRow(serverProblems);
    if (refused) {
      setTravelTo({ kind: "row", path: refused });
      return;
    }
    const control = anchorForField(saveError?.field ?? null);
    if (control) {
      setErrors((current) => ({ ...current, [control]: saveError?.message ?? "" }));
      setTravelTo({ kind: "anchor", key: control });
      return;
    }
    setTravelTo({ kind: "anchor", key: "submit" });
    // Read at the moment the failure lands, which is what `isError` marks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [create.isError]);

  /**
   * How many fields are in the way of a Save, rules and rows together.
   *
   * Counted as one number because it answers one question — *how much is left
   * before this saves* — and because the two used to be answered in different
   * places and at different times: the rules beside the button, the rows not
   * at all.
   */
  const outstandingCount = Object.keys(errors).length + problemByPath.size;
  /** How much of a refusal is readable in the table rather than the banner. */
  const refusalHint = refusalNotice(
    serverProblems.filter((problem) => problem.rowKey !== null).length,
  );

  /**
   * The one thing worth saying about the whole set of declared weights.
   *
   * **Across every block, not within one.** The fields are grouped into tables
   * by block for reading, but the Impact Factor is one weighted mean over the
   * whole profile — so a total computed per table would be a different number
   * in each and none of them the ranker's.
   *
   * Scored fields only, **and only those with a statistic actually chosen**. A
   * `carried` or `ignored` field has no say in the ranking, and neither does a
   * `stat` row whose statistic is still unset: `runner.py` keys the weight map by
   * the emitted stat key, so such a row reaches the ranker's divisor not at all.
   * Counting either would judge the total against a row that does not rank.
   *
   * Per-row shares were computed here too, for the line under each Weight box.
   * That line is gone, so this is the notice alone rather than a map nothing
   * reads.
   */
  const weightNoticeForReview = useMemo(() => {
    const scored = Object.entries(choices)
      .filter(([, choice]) => choice.role === "stat" && choice.statKey)
      .map(([path, choice]) => ({ key: path, weight: choice.weight }));
    return weightNotice(scored);
  }, [choices]);

  return (
    <Layout
      breadcrumbs={[{ label: "Sources", to: "/sources" }, { label: "Add a source" }]}
      title="Add a source"
      description="Point the portal at a JSON feed, say what its fields mean, and it becomes a Source Profile you can run."
      wide
    >
      <div className="flex flex-col gap-lg">
        {error && (
          <Banner tone="danger" title={error.message}>
            {typeof error.details.hint === "string" ? error.details.hint : null}
          </Banner>
        )}

        <Step
          number={1}
          title="Where is the data?"
          done={Boolean(discovery)}
          className="max-w-content"
        >
          <div className="flex flex-col gap-md">
            <PillGroup value={route} options={ROUTES} onChange={setRoute} />

            {route === "url" ? (
              <>
                {/*
                  The caption is the whole of one of the client's eight points.
                  It read "A JSON endpoint" over a placeholder ending in
                  `.json`, and they reported that the portal "only accepts
                  .json, which rejects a lot of the available API URLs".
                  Nothing has ever inspected a URL's extension — the Fetcher
                  decides by content type and by whether the body parses — so
                  the restriction did not exist and the caption invented it.
                */}
                <Field
                  label="Feed URL"
                  hint="Any address that answers with JSON. The extension does not matter — an ordinary REST endpoint with no .json in it works."
                >
                  <TextInput
                    value={url}
                    placeholder="https://api.example.org/v1/players?season=2025"
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </Field>
                <ClientIdentityField value={userAgent} onChange={setUserAgent} />
                {/* Beside the address rather than in the settings step: it is a
                    property of how this Feed is read, and someone who already
                    knows their source needs it should not have to save first
                    and come back. */}
                <PaginationChoice value={pagination} onChange={setPagination} />
                {sportField}
              </>
            ) : (
              <>
                {/* Above the menu it narrows, and absent when there is nothing
                    to narrow. Nothing saves it — it is not a field of the
                    profile — but it is no longer only about this one menu: it
                    narrows the chosen publisher's own choices too, because ESPN
                    is one entry over two sports and a football filter that left
                    MLS in its League parameter was a filter answering half the
                    question. See `leagueSportOptions` and `narrowLeagues` for
                    what it offers here, and `optionsFor` for what it does
                    below. */}
                {menuOptions.length > 0 && (
                  <Field
                    label="Filter by sport"
                    hint="Narrows the leagues below and the choices under them. Not the sport this source publishes into — that one follows from the league, and these settings settle it."
                  >
                    <Select value={menuSport} onChange={(e) => narrowBySport(e.target.value)}>
                      <option value="">All sports</option>
                      {menuOptions.map((option) => (
                        <option key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}

                {/* The first question, and it used to be the second. An
                    operator knows which league they want and not which of ten
                    publishers carries it; the old order made finding out a walk
                    through every source's menu, and two publishers carry NCAA
                    Division II. A combobox rather than a `<select>` because
                    fifty-four rows is past what the browser's own type-ahead can
                    answer — it matches from the start of the label and resets
                    after a second — and because a publisher's name is a thing
                    worth searching a league list by. */}
                {/* A menu that did not arrive, said once and loudly. Without
                    this the combobox renders empty and its search-miss message
                    blames the query — so every templated source is unreachable
                    and the screen reads as though the portal has no leagues.
                    The retry is the point: this endpoint composes nothing, so a
                    failure here is almost always transient. */}
                {leagues.isError && (
                  <Banner tone="danger" title="The league menu could not be read.">
                    {leagues.error instanceof ApiError
                      ? leagues.error.message
                      : "The portal could not reach its own source catalogue."}{" "}
                    Until it loads, <strong>Feed URL</strong> still adds a source by
                    address.
                    <div className="mt-xs">
                      <Button variant="secondary" onClick={() => void leagues.refetch()}>
                        {leagues.isFetching ? "Trying…" : "Try again"}
                      </Button>
                    </div>
                  </Banner>
                )}
                <Field
                  label="League"
                  hint="Search by league, by sport, or by the publisher you have access to. Which sources carry it is on each row."
                >
                  <Combobox
                    value={menuLeague}
                    rows={leagueOptions}
                    total={leagueTotal}
                    chosenLabel={chosenLeague?.display}
                    query={leagueQuery}
                    onQueryChange={setLeagueQuery}
                    onChange={chooseLeague}
                    label="League"
                    placeholder={
                      leagues.isPending ? "Reading the league menu…" : "Choose a league…"
                    }
                    searchPlaceholder="Search leagues, sports and publishers…"
                    noun="league"
                    disabled={leagues.isPending}
                    empty={
                      <>
                        No league matches “{leagueQuery.trim()}”
                        {menuSport !== "" && <> under {menuSport}</>}. Publishers are searched
                        too, so “pff” or “espn” lists what they carry.{" "}
                        <strong>Feed URL</strong> adds a source the portal has no template
                        for.
                      </>
                    }
                  />
                </Field>

                {/* Only once a league has been chosen, because until then there
                    is nothing to choose a publisher *of* — and a Source control
                    over the whole catalogue would be the question this screen
                    just stopped asking first. */}
                {chosenLeague && (
                  <Field
                    label="Source"
                    hint="Which publisher to read this league from. Its addresses are composed from what you choose below."
                  >
                    {/* `chosenSource`, not `templateKey`. They agree except
                        for the instant between a league changing and the state
                        that follows it, and a `<select>` whose value matches
                        none of its options renders **blank** — this frontend's
                        most-repeated defect. `sourceFor` resolves to the
                        league's first publisher rather than to nothing. */}
                    <Select
                      value={chosenSource?.template_key ?? ""}
                      onChange={(e) => chooseSource(e.target.value)}
                    >
                      {chosenLeague.sources.map((row) => (
                        <option key={row.template_key} value={row.template_key}>
                          {row.template_name}
                          {/* Said on the row, because it is what decides
                              whether this league is reachable today — and the
                              panel that proves it only appears after the
                              source is picked. */}
                          {credentialNote(row, templates.data)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                {/* What the form settled that the reader did not: a second
                    publisher worth knowing about, or a choice picked out of
                    several that compose the same league. Null on forty-nine of
                    the fifty-four rows, where there is nothing to report — see
                    `sourceNote`. */}
                {menuNote && <Muted>{menuNote}</Muted>}
                {(templates.isPending || leagues.isPending) && (
                  <Spinner label="Reading the source menu" />
                )}
                {/* The league menu and the template catalogue are two requests,
                    and only one of them had an error state. When the catalogue
                    is the one that fails the screen does not look broken — it
                    looks *finished*: the League and Source controls are built
                    from the menu, so they fill in normally, and everything that
                    comes off the template simply is not there. No description,
                    no credential panel, no parameters, no composed Feeds, and a
                    disabled Fetch with nothing saying why.
                    `credentialNote`'s fallback reinforces it by labelling every
                    row "(needs a sign-in)".

                    Reported from a real session: a league was chosen and Fetch
                    could not be clicked. The catalogue request had failed while
                    the API restarted, and a query that errors stays errored —
                    so the tab sat in that state until it was reloaded. A
                    silently empty step is worse than a visible failure, which
                    is the same argument the league-menu banner above makes. */}
                {templates.isError && (
                  <Banner tone="danger" title="The source catalogue could not be read.">
                    {templates.error instanceof ApiError
                      ? templates.error.message
                      : "The portal could not reach its own source catalogue."}{" "}
                    The league is chosen, but its settings, its sign-in and the
                    addresses it composes all come from the catalogue — so this
                    step cannot be completed until it loads.
                    <div className="mt-xs">
                      <Button variant="secondary" onClick={() => void templates.refetch()}>
                        {templates.isFetching ? "Trying…" : "Try again"}
                      </Button>
                    </div>
                  </Banner>
                )}
                {template && (
                  <>
                    <Muted>{template.description}</Muted>
                    {template.needs_credential && isBearer && (
                      <BearerPanel
                        publisher={template.publisher}
                        preconfigured={isPreconfigured}
                        overriding={overrideKey}
                        onOverride={() => {
                          setOverrideKey(true);
                          authenticate.reset();
                        }}
                        onUseConfigured={() => {
                          setOverrideKey(false);
                          setBearerToken("");
                          authenticate.reset();
                        }}
                        token={bearerToken}
                        onChange={(next) => {
                          setBearerToken(next);
                          authenticate.reset();
                        }}
                        signedIn={credentialId !== null}
                        busy={authenticate.isPending}
                        error={
                          authenticate.error instanceof ApiError
                            ? authenticate.error.message
                            : authenticate.error
                              ? "Could not verify this token."
                              : null
                        }
                        onSave={() => void authenticate.mutate()}
                        onForget={forgetSignIn}
                      />
                    )}
                    {template.needs_credential && !isBearer && (
                      <SignInPanel
                        publisher={template.publisher}
                        values={signIn}
                        onChange={setSignIn}
                        state={signInState}
                        refusal={
                          credential?.sign_in_message ??
                          (credentialQuery.error instanceof ApiError
                            ? `The sign-in could not be read back: ${credentialQuery.error.message}`
                            : null)
                        }
                        startError={authenticate.error}
                        onSignIn={() => void authenticate.mutate()}
                        onForget={forgetSignIn}
                        onSignInAgain={signInAgain}
                      />
                    )}
                    {/* Rendered from the declaration alone. Nothing in this
                        screen or in TemplateParameters branches on which
                        publisher was chosen — that is what makes a second
                        template cost no frontend change. */}
                    {/* The Sport filter reaches in here too. It narrows the
                        Source menu above, and ESPN is why that is not enough on
                        its own: one menu entry over a football surface and a
                        soccer one, so a football filter used to leave a League
                        menu offering MLS, NWSL and the Saudi Pro League. The
                        rule is `optionsFor`'s and the declaration is the
                        server's — see `TemplateOption.sport`. */}
                    <TemplateParameters
                      template={template}
                      values={values}
                      onChange={setValues}
                      invalid={composeError?.field}
                      message={composeError?.message}
                      sport={menuSport}
                    />
                    {sportField}
                    <ComposedFeeds
                      composing={composition.isFetching}
                      feeds={composed?.feeds ?? []}
                    />
                  </>
                )}
              </>
            )}
            <div ref={anchors.fetch as React.RefObject<HTMLDivElement>} tabIndex={-1}>
              <Button
                variant={discovery ? "secondary" : "primary"}
                disabled={!sourceUrl || busy || needsSignIn}
                onClick={() => void runDiscovery()}
              >
                {busy ? "Fetching…" : discovery ? "Fetch again" : "Fetch and read fields"}
              </Button>
              {scanFeeds.length > 1 && !scan && !discovery && (
                // Said before it starts, not only while it runs. A button that
                // silently takes half a minute is the thing being fixed here,
                // and a person deciding whether to press it wants the cost
                // first.
                <Muted className="mt-xs block">
                  Reads {scanFeeds.length} addresses — one per set of columns this source
                  publishes. Every conference under one set carries the same fields, so the
                  rest are not read.
                </Muted>
              )}
              <ScanProgress scan={scan} />
              {needsSignIn && (
                <Muted className="mt-xs block">
                  {/* Three wordings for the three credentials, matching the
                      button the reader is being sent to — the panel above says
                      "Use the configured key" where the deployment holds one,
                      and a hint telling them to verify a token they were never
                      shown names a control that is not on the screen. */}
                  {usingConfiguredKey
                    ? "Check the configured key above first. Fetch stays locked until the source accepts it."
                    : isBearer
                    ? "Verify the API token above first. Fetch stays locked until the source accepts it."
                    : signInState === "running" || signInState === "starting"
                      ? // Named rather than left as a spinner: a sign-in that
                        // drives a real browser through a publisher's login can
                        // take a minute and a half, and a control that is simply
                        // disabled reads as broken long before then.
                        `Waiting for ${template?.publisher ?? "the publisher"} to accept the sign-in. The fields below are read with it, and reading them without it would show the anonymous table.`
                      : "Sign in above first. The fields this reads are different signed out, and nothing on the screen afterwards would say so."}
                </Muted>
              )}
              {discoverError && (
                <Banner tone="danger" title={discoverError.message} className="mt-sm" />
              )}
            </div>
            {discovery && (
              <Field
                label="Where are the players?"
                /* Two sources, two sentences. Where every address answered
                   under one key there is a single list to name and the count
                   is that list's. Where they answered under several there is
                   no such list, and quoting the first address's key beside a
                   summed count would describe a source that does not exist. */
                hint={
                  new Set(discovery.feeds.map((feed) => feed.array_path)).size > 1
                    ? `Found ${discovery.record_count.toLocaleString()} records across ${
                        discovery.feeds.length
                      } Feeds, each under its own key. There is no single list to name, so leave this alone unless you know better.`
                    : `Found ${discovery.record_count} record${
                        discovery.record_count === 1 ? "" : "s"
                      } under "${discovery.array_path || "the document root"}". If that is the wrong list, pick the right one. Some sources publish a longer list of something else beside their players.`
                }
              >
                <RecordPathChoice
                  discovery={discovery}
                  recordPath={recordPath}
                  typing={typingRecordPath}
                  onTyping={setTypingRecordPath}
                  onChange={setRecordPath}
                  onPick={(path) => void runDiscovery({ recordPath: path })}
                />
              </Field>
            )}
          </div>
        </Step>

        {discovery && (
          <>
            <Step
              number={2}
              title="What is this source?"
              done={Boolean(name && league)}
              className="max-w-content"
            >
              <div className="flex flex-col gap-md">
                <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
                  <Field label="Name" error={errors.name}>
                    <TextInput
                      ref={anchors.name as React.RefObject<HTMLInputElement>}
                      value={name}
                      aria-invalid={errors.name ? true : undefined}
                      onChange={(e) => {
                        setName(e.target.value);
                        // From here the name is this person's, and no
                        // recompose may take it back. Emptying the field is
                        // not a decision, so clearing it hands the name back
                        // to the parameters rather than leaving it blank.
                        setNameIsTyped(e.target.value.trim() !== "");
                        clearError("name");
                      }}
                    />
                  </Field>
                  <Field label="League" hint="Appears in the filename." error={errors.league}>
                    <TextInput
                      ref={anchors.league as React.RefObject<HTMLInputElement>}
                      value={league}
                      placeholder="DII"
                      aria-invalid={errors.league ? true : undefined}
                      onChange={(e) => {
                        setLeague(e.target.value);
                        clearError("league");
                      }}
                    />
                  </Field>
                  <Field label="Season end year" hint="Declared, never guessed.">
                    <TextInput type="number" value={season} onChange={(e) => setSeason(Number(e.target.value))} />
                  </Field>
                </div>

                {discovery.blocks.length > 1 && (
                  <Field
                    label="Stat group"
                    hint="This source publishes the same statistics in several groups. Pick one, and it applies to every field at once."
                  >
                    <div className="flex flex-col gap-xs">
                      <Select value={scope} onChange={(e) => chooseGroup(e.target.value)}>
                        <option value="">All groups</option>
                        {discovery.blocks.map((block) => (
                          <option key={block} value={block}>
                            {block}
                          </option>
                        ))}
                      </Select>
                      <div>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<InfoIcon size={14} />}
                          onClick={() => setGroupsOpen(true)}
                        >
                          See what each group contains
                        </Button>
                      </div>
                    </div>
                    <StatGroupPreview
                      open={groupsOpen}
                      onClose={() => setGroupsOpen(false)}
                      groups={discovery.blocks}
                      // The snapshot only while it is of this source. Switching
                      // sources with a group still chosen reads only that group,
                      // and the last source's fields are no answer at all.
                      fields={
                        groupFields?.url === discovery.url ? groupFields.fields : discovery.fields
                      }
                      chosen={scope}
                      onChoose={(group) => {
                        chooseGroup(group);
                        setGroupsOpen(false);
                      }}
                    />
                  </Field>
                )}
              </div>
            </Step>

            <Step number={3} title="What do the fields mean?">
              <div className="flex flex-col gap-lg">
                <div className="flex flex-wrap items-center gap-xs">
                  <Badge tone="accent">{discovery.fields.length} fields</Badge>
                  <Badge tone="accent">{discovery.record_count.toLocaleString()} records</Badge>
                  <Badge tone="accent">
                    in <Code className="code-sm">
                      {discovery.array_path || "the document root"}
                    </Code>
                  </Badge>
                  {unknownCount > 0 && <Badge tone="warning">{unknownCount} set to ignore</Badge>}
                </div>

                <Muted>
                  You can save fields set to ignore as they are and work through them later, from
                  the source's own field review.
                </Muted>

                {/* Above the tables, with the identity banner and the weight
                    notice, because it is the same kind of statement: an answer
                    about the review as a whole rather than about any row in it.
                    The Role column below is where a field is *made* part of the
                    Identity Key; this is what happens when that key turns out
                    not to separate everybody, and the two belong on one screen.

                    Constrained to the composed width so it does not stretch to
                    the width of a six-hundred-field table. */}
                <div className="max-w-prose">
                  <RepeatedIdentityField
                    value={repeatedIdentity}
                    onChange={setRepeatedIdentity}
                  />
                </div>

                {/* The identity rule is not about one control — it is about the
                    Role column below — so its complaint sits above the tables
                    it refers to, and that is what Save scrolls to. */}
                {errors.identity && (
                  <div ref={anchors.identity as React.RefObject<HTMLDivElement>} tabIndex={-1}>
                    <Banner tone="danger" title="No player identity yet">
                      {errors.identity}
                    </Banner>
                  </div>
                )}

                {/* Above the tables for the same reason the identity rule is:
                    it is a statement about the Weight column as a whole, and a
                    banner inside one block's table would be a claim about that
                    block. Not an error and never blocks a Save.

                    **This fires on one state only**: a review where every
                    scored row carries a percentage and they do not add to 100.
                    It used to also warn about a review that mixed percentages
                    with blanks, and that was removed — the mixed state is a
                    normal and deliberate one, so the banner appeared during
                    ordinary work rather than on a mistake. With the per-row
                    share line gone from the Weight column, this banner is now
                    the only place the screen says anything about the set of
                    weights as a whole. */}
                {weightNoticeForReview && (
                  <Banner
                    tone={weightNoticeForReview.tone}
                    title={weightNoticeForReview.title}
                  >
                    {weightNoticeForReview.message}
                  </Banner>
                )}

                {Object.entries(grouped).map(([block, fields]) => (
                  <div key={block}>
                    {block && (
                      <div className="mb-xs">
                        <Label>{block}</Label>
                      </div>
                    )}
                    {/* One table per block, so each block's heading pins
                        while that block is on screen and is pushed off by the
                        next one's — which is what the grouping is for. */}
                    <TableScroll stickyHeader>
                      <table className="table table-flush table-dense">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Sample</th>
                            <th>Seen in</th>
                            {/* Guaranteed room for "Exclude from ranks", the longest Role.
                                Without it the Statistic column's `w-full` takes the
                                slack from here instead and leaves this a bare
                                chevron — the one control whose value says whether a
                                field is ranked at all. */}
                            <th className="min-w-[9.5rem]">Role</th>
                            {/* **The slack lands on Field, not on Statistic.** It used to go here, on
                                the argument that every other column holds a badge, a short select or a
                                number while this one holds a name — true while the table was capped at
                                1200px and every column was starved. Full width it is the wrong column:
                                measured at 1920px, Statistic took 673px to hold a 384px control, and on
                                a block where every row is *Ignore* it renders nothing at all and
                                reserved all of it anyway. A path is the longest and most variable thing
                                in the row — ESPN soccer publishes
                                `splits.categories.defensive.inneffectiveTackles` — so Field is where an
                                extra pixel buys something. */}
                            <th className="min-w-[14rem]">Statistic</th>
                            <th>Weight %</th>
                            {/* Here as well as on the Fields screen, because
                                this is where somebody first meets a source's
                                fields — the same reason the per-report control
                                was brought forward onto this screen. */}
                            {/* *Priority* rather than *Order*: the number sorts
                                rather than seats, so ten fields at 1 is a tie
                                and not ten first columns. */}
                            <th>
                              <HintedHeading label="Order priority" hint={ORDER_HINT} />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {fields.map((field) => {
                            const choice: FieldChoice = choices[field.path] ?? {
                              role: "ignored" as Role,
                              statKey: null,
                              weight: null,
                              sortOrder: null,
                              parseRule: {},
                            };
                            const problem = problemByPath.get(field.path) ?? null;
                            // Which targets published this path, and whether
                            // telling them apart is even a question. One report
                            // has nothing to separate; the rest of this portal
                            // is single-Feed and never sees any of it.
                            const reports = reportsByPath[field.path] ?? [];
                            const duplicated = reports.length > 1;
                            const overrides = perReport[field.path];
                            const splitHere = Boolean(overrides);
                            const open = expanded.has(field.path) || splitHere;
                            // Only a value can be read per report. A player's
                            // name, team and Identity Key are facts about the
                            // player, and the parser reads them from whichever
                            // Feed carries them whatever a scope says.
                            const scopable = choice.role === "carried" || choice.role === "stat";
                            const risk = expansionHazard({
                              statKey: choice.statKey,
                              // Nothing names an eligibility statistic yet —
                              // the profile does not exist — so the only
                              // hazard reachable here is the header one.
                              eligibilityStatKey: null,
                              entries: stats.map((entry) => ({
                                key: entry.key,
                                per_90_minutes_key: entry.per_90_minutes_key,
                              })),
                            });
                            const setOverride = (label: string, patch: Partial<FieldChoice>) =>
                              setPerReport((current) => {
                                const existing = current[field.path] ?? {};
                                // Materialise every report on the first
                                // override, so the saved shape is all-scoped
                                // rather than one scoped beside one not.
                                const seeded = Object.fromEntries(
                                  reports.map((name) => [name, existing[name] ?? choice]),
                                );
                                return {
                                  ...current,
                                  [field.path]: {
                                    ...seeded,
                                    [label]: { ...(existing[label] ?? choice), ...patch },
                                  },
                                };
                              });
                            return (
                              <Fragment key={field.path}>
                                <tr
                                  // Registered so a refusal naming this path can
                                  // scroll to it; dropped on unmount, because the
                                  // table is re-read and a stale node would be
                                  // travelled to and not found.
                                  ref={(node) => {
                                    if (node) rowAnchors.current.set(field.path, node);
                                    else rowAnchors.current.delete(field.path);
                                  }}
                                  // Focusable only while there is something to
                                  // travel to, and marked for the stylesheet.
                                  // Not `aria-invalid`, which says nothing on a
                                  // row: the reason below it is the `role=alert`
                                  // that gets announced.
                                  tabIndex={problem ? -1 : undefined}
                                  data-problem={problem ? "" : undefined}
                                >
                                  {/* `whitespace-nowrap` because `.code`
                                      carries `break-all`: once the disclosure
                                      control shares this cell, the auto-layout
                                      column can collapse and render every path
                                      one character per line. */}
                                  <td>
                                    <div className="flex items-center gap-xs whitespace-nowrap">
                                      {duplicated && !splitHere ? (
                                        <button
                                          type="button"
                                          className="shrink-0 rounded px-2xs text-ink-subtle hover:text-ink"
                                          aria-expanded={open}
                                          aria-label={`${open ? "Hide" : "Show"} the ${reports.length} Feeds publishing ${field.path}`}
                                          onClick={() =>
                                            setExpanded((current) => {
                                              const next = new Set(current);
                                              if (next.has(field.path)) next.delete(field.path);
                                              else next.add(field.path);
                                              return next;
                                            })
                                          }
                                        >
                                          {open ? "▾" : "▸"}
                                        </button>
                                      ) : (
                                        <span
                                          className="inline-block w-[1.25rem] shrink-0"
                                          aria-hidden="true"
                                        />
                                      )}
                                      {/* `title` rather than `Tooltip`; see
                                          the Fields screen for the bubble
                                          that covered the next row. */}
                                      <Code
                                        className="max-w-[22rem] truncate"
                                        title={field.path}
                                      >
                                        {field.path}
                                      </Code>
                                    </div>
                                  </td>
                                  <td>
                                    <SampleCell
                                      value={
                                        splitHere ? "—" : String(field.samples[0] ?? "—")
                                      }
                                    />
                                  </td>
                                  <td>
                                    {duplicated ? (
                                      <div className="flex items-center gap-xs whitespace-nowrap">
                                        <Badge tone={splitHere ? "accent" : "warning"}>
                                          {/* The accent tone carries "read
                                              apart"; the button beside it says
                                              so in words. */}
                                          {`${reports.length} Feeds`}
                                        </Badge>
                                        {splitHere ? (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            title="Read every Feed under one Binding again. The first answer stands for all of them and the per-Feed ones are discarded."
                                            onClick={() =>
                                              setPerReport((current) => {
                                                const next = { ...current };
                                                delete next[field.path];
                                                return next;
                                              })
                                            }
                                          >
                                            Merge
                                          </Button>
                                        ) : (
                                          scopable && (
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              disabled={Boolean(risk)}
                                              title={risk ? risk.message : `${reports.length} Feeds publish this path, and it reaches the file once per Feed either way. Give each one its own statistic where they mean different things — which is how one report's ‘interceptions thrown’ and another's ‘caught’ get opposite Directions.`}
                                              onClick={() => {
                                                const matched = autoMatchReports(
                                                  field.path,
                                                  reports.map((label) => ({
                                                    feedId: label,
                                                    label,
                                                  })),
                                                  choice.role === "stat" ? stats : [],
                                                );
                                                setPerReport((current) => ({
                                                  ...current,
                                                  [field.path]: Object.fromEntries(
                                                    reports.map((label) => [
                                                      label,
                                                      matched[label]
                                                        ? { ...choice, statKey: matched[label]! }
                                                        : choice,
                                                    ]),
                                                  ),
                                                }));
                                                setExpanded((current) =>
                                                  new Set(current).add(field.path),
                                                );
                                              }}
                                            >
                                              Per Feed
                                            </Button>
                                          )
                                        )}
                                      </div>
                                    ) : (
                                      <Muted>
                                        {field.occurrences.toLocaleString()} /{" "}
                                        {discovery.record_count.toLocaleString()}
                                      </Muted>
                                    )}
                                  </td>
                                  {/* Hidden once the field is read apart, for
                                      the reason `ReviewFields` hides the same
                                      cells on a materialised field: with every
                                      Feed carrying its own answer,
                                      `draftsForField` builds the saved Bindings
                                      from the overrides alone and never reads
                                      `choice`. Leaving the controls here offers
                                      an edit that changes no column — and the
                                      commonest shape of it, *Statistic* with
                                      nothing chosen, marks the field with a
                                      `stat_without_statistic` refusal whose
                                      only two remedies are the two controls
                                      that should not have been there.

                                      Hidden rather than cleared: `choices`
                                      still holds the field's own answer, which
                                      is what Merge puts back. */}
                                  {splitHere ? (
                                    <td colSpan={4}>
                                      <Muted>Each Feed below is read on its own.</Muted>
                                    </td>
                                  ) : (
                                    <>
                                      <td>
                                        <SelectCompact
                                          value={choice.role}
                                          onChange={(e) => {
                                            const next = {
                                              ...choices,
                                              [field.path]: { ...choice, role: e.target.value as Role },
                                            };
                                            setChoices(next);
                                            // Only once the change actually answers the
                                            // complaint; clearing on any edit would
                                            // retract it while it is still true.
                                            if (identityCount(next) > 0) clearError("identity");
                                          }}
                                        >
                                          {ROLE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                              {option.label}
                                            </option>
                                          ))}
                                        </SelectCompact>
                                      </td>
                                      <td>
                                        {choice.role === "stat" ? (
                                          <StatisticCell
                                            groupLabels={groupLabels}
                                            value={choice.statKey}
                                            weight={choice.weight}
                                            entries={stats}
                                            label={`Statistic for ${field.path}`}
                                            newOpen={newStatFor === field.path}
                                            // The weight travels with the key, the
                                            // same way discovery's own suggestion
                                            // carries `default_weight` onto a field
                                            // it matched. A weight already typed
                                            // into this row survives the choice.
                                            onChange={(statKey, weight) =>
                                              setChoices({
                                                ...choices,
                                                [field.path]: { ...choice, statKey, weight },
                                              })
                                            }
                                            onNew={() =>
                                              setNewStatFor((current) =>
                                                current === field.path ? null : field.path,
                                              )
                                            }
                                          />
                                        ) : field.already_bound ? (
                                          <Badge>already bound</Badge>
                                        ) : null}
                                      </td>
                                      {/* Blank rather than 1 where nothing is
                                          declared, and the two rank identically:
                                          ADR-0040 keeps them distinct review states
                                          so the screen can show which one a row is
                                          in, and filling them all in would show a
                                          decision against every row where somebody
                                          made a few. */}
                                      <td>
                                        {choice.role === "stat" && (
                                          <WeightCell
                                            path={field.path}
                                            weight={choice.weight}
                                            onChange={(weight) =>
                                              setChoices({
                                                ...choices,
                                                [field.path]: { ...choice, weight },
                                              })
                                            }
                                          />
                                        )}
                                      </td>
                                      {/* Offered on every role, unlike Weight: a
                                          field moved between Statistic and
                                          *Exclude from ranks* moves between the two
                                          blocks that publish columns, and a cell
                                          that disappeared on the way would lose the
                                          placement in the gap. The cell disables
                                          itself for a role that publishes none. */}
                                      <td>
                                        <OrderCell
                                          path={field.path}
                                          sortOrder={choice.sortOrder}
                                          role={choice.role}
                                          onChange={(sortOrder) =>
                                            setChoices({
                                              ...choices,
                                              [field.path]: { ...choice, sortOrder },
                                            })
                                          }
                                        />
                                      </td>
                                    </>
                                  )}
                                </tr>
                                {problem && <ProblemNote colSpan={7} message={problem} />}
                                {!splitHere && choice.role === "stat" && newStatFor === field.path && (
                                  <NewStatisticRow colSpan={7}>
                                    <NewStatisticForm
                                      sport={sport}
                                      // This screen creates the profile, so
                                      // there is none to write to and no Run to
                                      // read labels from. The draft supplies
                                      // both: discovery's reading of the
                                      // payload, and a map saved with the
                                      // source rather than now.
                                      profileExists={false}
                                      draft={positionDraft}
                                      entries={stats}
                                      onCancel={() => setNewStatFor(null)}
                                      onCreated={(statKey, weight) => {
                                        // Selected on the row it was created
                                        // from, and nowhere else: spreading
                                        // `choices` keeps every other row's
                                        // answer, which is the whole reason this
                                        // is not a trip to the Stat Dictionary
                                        // tab and back.
                                        //
                                        // The weight travels with the key, so a
                                        // statistic invented during Add Source
                                        // can be weighted in the same breath
                                        // rather than on a later visit to the
                                        // Fields screen.
                                        //
                                        // A blank Weight leaves this row's own
                                        // alone rather than clearing it: setting
                                        // it unconditionally silently discarded
                                        // a weight already typed into the cell.
                                        setChoices({
                                          ...choices,
                                          [field.path]: {
                                            ...choice,
                                            statKey,
                                            ...(weight == null ? {} : { weight }),
                                          },
                                        });
                                        setNewStatFor(null);
                                      }}
                                    />
                                  </NewStatisticRow>
                                )}

                                {/* One line per report, each showing what that
                                    report would be read as and what it actually
                                    published. Inherited until somebody changes
                                    it, which is what keeps `penalties` one
                                    decision across thirteen reports instead of
                                    thirteen identical ones.

                                    Nothing per report is stored until an
                                    override exists — `perReport` has no entry
                                    for this path — so opening the disclosure to
                                    look costs nothing. */}
                                {duplicated &&
                                  open &&
                                  reports.map((label) => {
                                    const child = overrides?.[label] ?? choice;
                                    const inherited = !overrides;
                                    const sample = samplesByPath[field.path]?.[label]?.[0];
                                    const childKey = childRowKey(field.path, label);
                                    // A split field is judged per Feed, so this
                                    // is where its complaints land — and where
                                    // a refused Save has to be able to travel
                                    // to, which is what the anchor is for.
                                    const childProblem = problemByPath.get(childKey) ?? null;
                                    return (
                                      <Fragment key={childKey}>
                                        <tr
                                          data-child=""
                                          ref={(node) => {
                                            if (node) rowAnchors.current.set(childKey, node);
                                            else rowAnchors.current.delete(childKey);
                                          }}
                                          tabIndex={childProblem ? -1 : undefined}
                                          data-problem={childProblem ? "" : undefined}
                                        >
                                          <td>
                                            <div className="flex items-center gap-xs whitespace-nowrap pl-lg">
                                              <span className="text-ink-subtle" aria-hidden="true">
                                                └
                                              </span>
                                              <span className="text-body-sm text-ink">{label}</span>
                                            </div>
                                          </td>
                                          {/* This report's own value. The merged
                                              row above shows whichever report was
                                              read first, which is exactly what
                                              hides a disagreement. */}
                                          <td>
                                            <SampleCell
                                              value={sample === undefined ? "—" : String(sample)}
                                            />
                                          </td>
                                          <td>
                                            <Badge tone={inherited ? "neutral" : "accent"}>
                                              {inherited ? "inherited" : "own statistic"}
                                            </Badge>
                                          </td>
                                          <td>
                                            <SelectCompact
                                              value={child.role}
                                              onChange={(e) =>
                                                setOverride(label, {
                                                  role: e.target.value as Role,
                                                })
                                              }
                                            >
                                              {ROLE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                  {option.label}
                                                </option>
                                              ))}
                                            </SelectCompact>
                                          </td>
                                          <td>
                                            {/* The Direction is in the menu's own
                                                label — `statOptions` appends
                                                "(lower is better)". A badge
                                                beside it repeated that in 92px
                                                and squeezed the menu to a bare
                                                chevron. */}
                                            {child.role === "stat" && (
                                              <StatisticCell
                                                groupLabels={groupLabels}
                                                value={child.statKey}
                                                weight={child.weight}
                                                entries={stats}
                                                label={`Statistic for ${field.path} in ${label}`}
                                                newOpen={newStatFor === childKey}
                                                onChange={(statKey, weight) =>
                                                  setOverride(label, { statKey, weight })
                                                }
                                                onNew={() =>
                                                  setNewStatFor((current) =>
                                                    current === childKey ? null : childKey,
                                                  )
                                                }
                                              />
                                            )}
                                          </td>
                                          <td>
                                            {child.role === "stat" && (
                                              <WeightCell
                                                path={`${field.path} (${label})`}
                                                weight={child.weight}
                                                onChange={(weight) => setOverride(label, { weight })}
                                              />
                                            )}
                                          </td>
                                          {/* A split report publishes its own
                                              column, so it has its own position.
                                              The value shown until one is
                                              overridden is the field's, because
                                              that is the position its column
                                              has. */}
                                          <td>
                                            <OrderCell
                                              path={`${field.path} (${label})`}
                                              sortOrder={child.sortOrder}
                                              role={child.role}
                                              onChange={(sortOrder) =>
                                                setOverride(label, { sortOrder })
                                              }
                                            />
                                          </td>
                                        </tr>

                                        {childProblem && (
                                          <ProblemNote colSpan={7} message={childProblem} />
                                        )}

                                        {child.role === "stat" && newStatFor === childKey && (
                                          <NewStatisticRow colSpan={7}>
                                            <NewStatisticForm
                                              sport={sport}
                                              profileExists={false}
                                              draft={positionDraft}
                                              entries={stats}
                                              onCancel={() => setNewStatFor(null)}
                                              onCreated={(statKey, weight) => {
                                                // Onto this Feed's override and
                                                // not the field's. A statistic
                                                // invented on a per-Feed line is
                                                // being invented for that Feed —
                                                // which is the whole reason the
                                                // field was read apart — and
                                                // writing it to `choices` left
                                                // the line that asked for it
                                                // showing "Choose a statistic…".
                                                //
                                                // A blank Weight leaves this
                                                // line's own alone; see the
                                                // field's own form above.
                                                setOverride(label, {
                                                  statKey,
                                                  ...(weight == null ? {} : { weight }),
                                                });
                                                setNewStatFor(null);
                                              }}
                                            />
                                          </NewStatisticRow>
                                        )}
                                      </Fragment>
                                    );
                                  })}

                                {/* Said once, under the last report, rather
                                    than against every one of thirteen. */}
                                {/* Only ever a reason the field must not be
                                    split — see `ExpansionHazard`. */}
                                {duplicated && open && !splitHere && risk && (
                                  <ProblemNote colSpan={7} message={risk.message} />
                                )}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </TableScroll>
                  </div>
                ))}

                {saveError && (
                  <div ref={anchors.submit as React.RefObject<HTMLDivElement>} tabIndex={-1}>
                    <Banner tone="danger" title={saveError.message}>
                      {/* The refusal's own hint where it carries one, and
                          otherwise where to look: every problem naming a field
                          is marked against that field in the table above, which
                          the banner would otherwise be describing from a
                          distance of several hundred rows. */}
                      {typeof saveError.details.hint === "string"
                        ? saveError.details.hint
                        : refusalHint}
                    </Banner>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-sm">
                  <Button variant="primary" disabled={busy} onClick={() => void save()}>
                    {busy ? "Saving…" : "Save source"}
                  </Button>
                  {outstandingCount > 0 && (
                    <Muted tone="text-danger-ink">
                      {outstandingCount === 1
                        ? "One field needs attention before you can save this."
                        : `${outstandingCount} fields need attention before you can save this.`}
                    </Muted>
                  )}
                </div>
              </div>
            </Step>
          </>
        )}
      </div>
    </Layout>
  );
}

/**
 * A numbered step. The mark fills in once the step is satisfied — the only
 * place in the portal where a card header carries a graphic, and it earns it:
 * this screen is the one linear sequence in an otherwise flat panel.
 */
function Step({
  number,
  title,
  done = false,
  children,
  className,
}: {
  number: number;
  title: string;
  done?: boolean;
  children: ReactNode;
  /**
   * Narrow this step, for one that holds a form rather than a table.
   *
   * The screen opts out of the 1200px cap so step 3 can lay six hundred
   * Bindings across the window. Steps 1 and 2 are forms — four menus and
   * four fields — and a form card that wide is the empty space this screen
   * was widened to remove, moved inside the border.
   *
   * It is the **card** that narrows and never the control inside it. Capping
   * the control instead was the first shape of this, and it left each menu
   * short of the card's own edge with a band of nothing beside it, which is
   * the same complaint one level in.
   */
  className?: string;
}) {
  return (
    <Card
      className={className}
      title={
        <span className="inline-flex items-center gap-sm">
          <span
            aria-hidden="true"
            className={cx(
              "inline-flex size-[24px] shrink-0 items-center justify-center rounded-full font-display text-caption",
              done
                ? "border-none bg-primary text-on-primary"
                : "border border-hairline bg-surface-card text-muted",
            )}
          >
            {number}
          </span>
          {title}
        </span>
      }
    >
      {children}
    </Card>
  );
}

/**
 * The addresses the template built, shown because they are the thing being
 * agreed to.
 *
 * Not hidden on the grounds that nobody should have to read them: the whole
 * claim is that these are the right addresses, and thirteen of them appearing at
 * once is how somebody checks that changing the season changed all thirteen. The
 * URL is secondary to the report name, so it is set small and allowed to
 * overflow rather than wrapping each row to three lines.
 */
/**
 * A static bearer token, before the publisher's fields are read.
 *
 * Same place in the flow as SignInPanel — credential before discovery — but
 * the secret shape is a token rather than a login form. Which panel appears is
 * declared on the template (`credential_type`), not branched on publisher name.
 */
function BearerPanel({
  publisher,
  preconfigured,
  overriding,
  onOverride,
  onUseConfigured,
  token,
  onChange,
  signedIn,
  busy,
  error,
  onSave,
  onForget,
}: {
  publisher: string;
  /** This deployment holds the key already, so there is nothing to type.
   *  The server resolves it from its own configuration; the key has never
   *  been in this browser and is not about to be. */
  preconfigured: boolean;
  /** The operator asked to supply a key of their own anyway. Meaningless
   *  unless `preconfigured` — without a portal key the field is the only
   *  route, and this is what distinguishes "no key here" from "a different
   *  key for this source". */
  overriding: boolean;
  onOverride: () => void;
  onUseConfigured: () => void;
  token: string;
  onChange: (next: string) => void;
  signedIn: boolean;
  busy: boolean;
  error: string | null;
  onSave: () => void;
  onForget: () => void;
}) {
  if (signedIn) {
    return (
      <Banner tone="success" title={`API token verified for ${publisher}`}>
        <div className="flex flex-wrap items-center gap-sm">
          <span>
            The token reached the source successfully. Discovery and Runs will
            present it; saving the source keeps it attached to the profile.
          </span>
          <Button size="sm" variant="ghost" onClick={onForget}>
            Use a different token
          </Button>
        </div>
      </Banner>
    );
  }

  // The deployment's own key, with the paste route still open beside it.
  //
  // The configured key is a **default, not a lock**. One portal holds one
  // account's key, and the reasons to want another are ordinary: a second PFF
  // account, a key being rotated, or a profile that should read a different
  // subscription from the rest. So this offers both, and the server already
  // settles which wins — a posted secret beats `from_template`.
  //
  // Either way there is a button, because the key is proved against the
  // composed Feed before Fetch unlocks, and a configured key can be as wrong as
  // a pasted one: expired, revoked, or for an account whose subscription
  // lapsed. That has to surface here rather than two steps later against an
  // empty field table.
  if (preconfigured && !overriding) {
    return (
      <div className="flex flex-col gap-md rounded-lg border border-hairline-soft px-md py-md">
        <div>
          <Label>API key for {publisher}</Label>
          <Muted className="mt-xxs block">
            This portal is configured with an API key for {publisher}, so there
            is nothing to enter. Checking it reaches the source before Fetch
            unlocks — an expired or revoked key fails here, not on the field
            table.
          </Muted>
        </div>
        {error && (
          <Banner tone="danger" title={`${publisher} refused the configured key`}>
            {error}
          </Banner>
        )}
        <div className="flex flex-wrap items-center gap-xs">
          <Button variant="primary" disabled={busy} onClick={onSave}>
            {busy ? "Checking…" : "Use the configured key"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onOverride}>
            Use a different key
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-md rounded-lg border border-hairline-soft px-md py-md">
      <div>
        <Label>API token for {publisher}</Label>
        <Muted className="mt-xxs block">
          {preconfigured
            ? `This key is used for this source only, in place of the one configured on the portal.`
            : `A static bearer token this publisher issues.`}{" "}
          It is checked against the source before Fetch unlocks — a wrong token
          stays here, not on the field table.
        </Muted>
      </div>
      <Field label="Bearer token" error={error ?? undefined}>
        <TextInput
          type="password"
          autoComplete="off"
          value={token}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
      <div className="flex flex-wrap items-center gap-xs">
        <Button variant="primary" disabled={token.trim() === "" || busy} onClick={onSave}>
          {busy ? "Verifying…" : "Verify token"}
        </Button>
        {/* Only offered where there is something to go back to. */}
        {preconfigured && (
          <Button size="sm" variant="ghost" onClick={onUseConfigured}>
            Use the portal's key instead
          </Button>
        )}
      </div>
    </div>
  );
}


/**
 * Signing in to the publisher, before its fields are read.
 *
 * It sits inside step one rather than being a step of its own because it is
 * not a separate decision — it is part of *where the data is*, on a source
 * where the address alone does not reach the data anybody is paying for.
 *
 * The panel is deliberately small: an email, a password, and a button. The
 * sign-in address comes from the template where the publisher declared one, so
 * the usual case asks for the two things a subscriber already knows and
 * nothing they would have to go and look up.
 */
function SignInPanel({
  publisher,
  values,
  onChange,
  state,
  refusal,
  startError,
  onSignIn,
  onForget,
  onSignInAgain,
}: {
  publisher: string;
  values: { username: string; password: string; loginUrl: string };
  onChange: (next: { username: string; password: string; loginUrl: string }) => void;
  /** Where the sign-in has got to (ADR-0033). Four states rather than a
   *  boolean, because "a Credential Set exists" and "it signed in" stopped
   *  being the same thing when the browser work moved off the request. */
  state: "none" | "starting" | "running" | "failed" | "signed-in";
  /** The acquirer's own sentence, where it refused. Rendered rather than
   *  summarised: "the password was wrong" and "the sign-in page could not be
   *  reached" call for opposite actions, and only the server can tell them
   *  apart. */
  refusal: string | null;
  /** A create that was refused outright — a malformed secret, an acquirer the
   *  portal does not have. Distinct from `refusal`, which is the sign-in
   *  itself failing after a Credential Set was accepted. */
  startError: unknown;
  onSignIn: () => void;
  onForget: () => void;
  onSignInAgain: () => void;
}) {
  if (state === "signed-in") {
    return (
      <Banner tone="success" title={`Signed in to ${publisher}`}>
        <div className="flex flex-wrap items-center gap-sm">
          <span>
            The fields read below are the ones this subscription carries. Saving keeps
            the sign-in, and the portal renews it by itself when it lapses.
          </span>
          <Button size="sm" variant="ghost" onClick={onForget}>
            Use a different account
          </Button>
        </div>
      </Banner>
    );
  }

  // The wait, named. The portal drives a real browser through the publisher's
  // own login — two form steps, sometimes an interstitial — which against some
  // publishers is a minute and a half. A bare spinner over that long reads as a
  // hung screen, so this says who is being waited for and that nothing further
  // can happen yet.
  if (state === "starting" || state === "running") {
    return (
      <Banner tone="info" title={`Signing in to ${publisher}`}>
        The portal is signing in to {publisher} in the background. This can take up
        to a couple of minutes — it works through the publisher's own login page
        rather than an API. Nothing below can be read until it finishes, because
        the fields are different signed out.
      </Banner>
    );
  }

  if (state === "failed") {
    return (
      <Banner tone="danger" title={`Could not sign in to ${publisher}`}>
        <div className="flex flex-col gap-sm">
          <span>{refusal ?? "The sign-in did not succeed."}</span>
          <div>
            <Button size="sm" variant="secondary" onClick={onSignInAgain}>
              Try again
            </Button>
          </div>
        </div>
      </Banner>
    );
  }

  // Everything the credential needs, or the button would post a secret the
  // server is only going to refuse.
  const ready =
    values.username.trim() !== "" && values.password !== "" && values.loginUrl.trim() !== "";

  return (
    <div className="flex flex-col gap-md rounded-lg border border-hairline-soft px-md py-md">
      <div>
        <Label>Sign in to {publisher}</Label>
        <Muted className="mt-xxs block">
          This source sells columns rather than access: without signing in it answers
          with every player and none of the statistics. Signing in first makes the
          fields below the right ones to classify.
        </Muted>
      </div>
      <Field label="Email">
        <TextInput
          type="email"
          autoComplete="off"
          value={values.username}
          onChange={(e) => onChange({ ...values, username: e.target.value })}
        />
      </Field>
      <Field label="Password">
        <TextInput
          type="password"
          autoComplete="new-password"
          value={values.password}
          onChange={(e) => onChange({ ...values, password: e.target.value })}
        />
      </Field>
      {/* Shown only where the template did not declare one. A publisher the
          portal knows how to reach should not be asking a subscriber to go and
          find its login page. */}
      {values.loginUrl === "" && (
        <Field
          label="Sign-in page"
          hint="The address of this publisher's login form. The portal submits the email and password to it."
        >
          <TextInput
            type="url"
            placeholder="https://…/login"
            value={values.loginUrl}
            onChange={(e) => onChange({ ...values, loginUrl: e.target.value })}
          />
        </Field>
      )}
      {startError instanceof ApiError && (
        <Banner tone="danger" title="That sign-in was not accepted">
          {startError.message}
        </Banner>
      )}
      <div>
        {/* No busy state here: the panel is replaced wholesale the moment the
            sign-in starts, so this button only ever renders while there is
            nothing in flight. */}
        <Button variant="primary" disabled={!ready} onClick={onSignIn}>
          Sign in
        </Button>
      </div>
    </div>
  );
}


/**
 * How far through the field scan we are.
 *
 * A determinate bar rather than a spinner, and that is the whole point of it:
 * this scan is one request per set of columns the source publishes — seven for a
 * Wyscout profile — and each is a round trip to the publisher. A spinner over
 * half a minute of that is indistinguishable from a hang, which is what sent
 * somebody to press the button again and then go looking for the eighty-three
 * fields it had not fetched yet.
 *
 * The count and the name are both shown because they answer different
 * questions: "5 of 7" says how much longer, "Key Passing" says that it is
 * moving. Neither alone does both.
 */
function ScanProgress({
  scan,
}: {
  scan: { done: number; total: number; label: string; attempt: number; elapsedMs: number } | null;
}) {
  if (!scan) return null;
  // One address is a wait nobody needs a bar for; the spinner on the button
  // already says it is working — **until it doesn't**. A single pasted address
  // can be the two-minute one, and that is the case with no other sign of
  // progress on screen at all, so the counter still earns its place once the
  // wait is long enough to be worth explaining.
  if (scan.total <= 1) {
    return scan.elapsedMs >= 5000 ? (
      <Muted className="ml-sm">Still reading {scan.label} — {Math.round(scan.elapsedMs / 1000)}s.</Muted>
    ) : null;
  }
  /**
   * What is left *after* the one being read, which is not `total - done`.
   *
   * The two halves of the sentence below count from different places. "10 of
   * 13" names the address currently in flight, so that address is inside the
   * 10; `total - done` treats it as not yet started, so it was inside the
   * remainder too. Reading the tenth of thirteen printed "10 of 13. 4
   * addresses left", which adds to fourteen.
   *
   * Counted once, from the number already on screen.
   */
  const remaining = Math.max(0, scan.total - scan.done - 1);
  return (
    <div className="mt-xs flex flex-col gap-xs" role="status" aria-live="polite">
      <div
        className="progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={scan.total}
        aria-valuenow={scan.done}
        aria-label="Reading the source's addresses"
      >
        <div
          className="progress-bar"
          style={{ width: `${Math.round((scan.done / scan.total) * 100)}%` }}
        />
      </div>
      <Muted>
        Reading {scan.label} — {scan.done + 1} of {scan.total}.{" "}
        {remaining === 0
          ? "The last one."
          : remaining === 1
            ? "One address left after it."
            : `${remaining} addresses left after it.`}
        {/* Said only while it is happening. A scan that silently waited four
            seconds and tried again looked like one that had hung, which is
            the state this whole retry is meant to get somebody out of. */}
        {scan.attempt > 1 && (
          <>
            {" "}
            <strong className="text-ink">
              That address did not answer — attempt {scan.attempt} of {MAX_ATTEMPTS}.
            </strong>
          </>
        )}
        {/* Counted up only once the wait is long enough to be worth
            explaining. Some of these addresses answer in under a second and a
            timer on those is noise; `defense/pass_rush` takes minutes, and on
            that one a number that moves is the difference between waiting and
            pressing the button again. */}
        {scan.attempt === 1 && scan.elapsedMs >= 5000 && (
          <> Still reading — {Math.round(scan.elapsedMs / 1000)}s.</>
        )}
      </Muted>
    </div>
  );
}

function ComposedFeeds({
  feeds,
  composing,
}: {
  feeds: { url: string; label: string }[];
  composing: boolean;
}) {
  if (composing && feeds.length === 0) return <Spinner label="Composing the Feeds" />;
  if (feeds.length === 0) return null;
  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-wrap items-center gap-xs">
        <Label>
          {feeds.length === 1 ? "One Feed" : `${feeds.length} Feeds`}, composed
        </Label>
        {composing && <Muted>recomposing…</Muted>}
      </div>
      {/* Capped rather than allowed to run: a Wyscout composition is 175 Feeds
          of four-hundred-character addresses, which buried the name field, the
          credential panel and the Save button under dozens of screens of URLs.
          `ScrollPanel` frames it so the cap reads as "there is more in here"
          rather than as a list that stops. */}
      <ScrollPanel max="max-h-[22rem]">
        <TableScroll>
          <table className="table table-flush">
            <tbody>
              {feeds.map((feed) => (
                <tr key={feed.url}>
                  <td className="whitespace-nowrap">{feed.label}</td>
                  <td>
                    <Code className="code-sm">{feed.url}</Code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      </ScrollPanel>
    </div>
  );
}

function groupByBlock(fields: DiscoveredField[]): Record<string, DiscoveredField[]> {
  const grouped: Record<string, DiscoveredField[]> = {};
  for (const field of fields) {
    const key = field.block ?? "";
    (grouped[key] ??= []).push(field);
  }
  return grouped;
}

/** Fields explicitly marked as identity, or the name and team as a fallback. */
function deriveIdentity(choices: Record<string, FieldChoice>): string[] {
  const explicit = Object.entries(choices)
    .filter(([, c]) => c.role === "identity")
    .map(([path]) => path);
  if (explicit.length > 0) return explicit;
  return Object.entries(choices)
    .filter(([, c]) => c.role === "player_name" || c.role === "team")
    .map(([path]) => path);
}

function identityCount(choices: Record<string, FieldChoice>): number {
  return deriveIdentity(choices).length;
}

/**
 * The rejection an API call carries, kept as an `ApiError` so the form can
 * still render `error.field` against the control that caused it.
 */
function asApiError(error: unknown): ApiError | null {
  if (!error) return null;
  return error instanceof ApiError ? error : new ApiError(0, "unknown", "Something went wrong.");
}


/** The menu entry that turns the players' list back into a typed path. */
const TYPED_PATH = "\u0000typed";

/**
 * "Where are the players?" as a menu of the lists the payload actually holds.
 *
 * A person adding a source has never seen its JSON, so asking them to type a
 * path asked for the one thing they could not know. The menu offers every
 * candidate the server found, with how many entries each holds — players are
 * nearly always the long one. Typing stays available for the case the scan
 * cannot list, a path the source only fills in on some pages.
 *
 * The document root is the empty path, which the fetch also reads as "not
 * declared". Harmless: a root that is itself the list leaves nothing else to
 * find.
 *
 * **A source whose Feeds disagree must not be asked this question.** The
 * candidates are the *first* Feed's — a record path is declared once per
 * profile, so the server sends one list rather than thirteen — and on PFF the
 * thirteen reports each publish under their own key: `passing_summary`,
 * `receiving_summary`, `kicking_summary`. Declaring the first would leave the
 * other twelve finding nothing, and the menu offered exactly that, preselected,
 * beside a count from one report that reads as the whole division's.
 *
 * So where the Feeds came back under different keys the first option is
 * *Each Feed's own list*, which is the empty path and therefore no declaration
 * at all — the payload reader then finds the single wrapping array in each
 * response on its own. That is what the working PFF profile has always done;
 * it stores `record_path = null`. The old menu reached the same place only by
 * never being touched.
 */
function RecordPathChoice({
  discovery,
  recordPath,
  typing,
  onTyping,
  onChange,
  onPick,
}: {
  discovery: DiscoveryResult;
  recordPath: string;
  typing: boolean;
  onTyping: (typing: boolean) => void;
  onChange: (path: string) => void;
  onPick: (path: string) => void;
}) {
  const candidates = discovery.record_arrays ?? [];
  // What each Feed actually answered under. More than one distinct key means no
  // single path can name them all, and the question changes from "which list?"
  // to "should a list be named at all?".
  const feedPaths = new Set(discovery.feeds.map((feed) => feed.array_path));
  const feedsDisagree = feedPaths.size > 1;
  // Undeclared is the right answer there, so it is what an untouched control
  // shows — `recordPath` is already "" until somebody chooses, and this stops
  // the menu displaying the first Feed's key as though it had been chosen.
  const current = feedsDisagree ? recordPath : recordPath || discovery.array_path;
  const listed =
    (feedsDisagree && current === "") ||
    candidates.some((candidate) => candidate.path === current);
  const showInput = typing || !listed;

  return (
    <div className="flex flex-col gap-xs">
      {feedsDisagree && (
        <Muted>
          These {discovery.feeds.length} Feeds publish their players under{" "}
          {feedPaths.size} different keys, so no one path names them all. Leave
          this on <strong>Each Feed's own list</strong> unless you know
          otherwise — naming one would leave the other{" "}
          {discovery.feeds.length - 1} finding nothing.
        </Muted>
      )}
      {(candidates.length > 0 || feedsDisagree) && (
        <Select
          value={showInput ? TYPED_PATH : current}
          onChange={(e) => {
            const value = e.target.value;
            if (value === TYPED_PATH) {
              onTyping(true);
              return;
            }
            onTyping(false);
            onChange(value);
            onPick(value);
          }}
        >
          {/* First and default where the Feeds disagree. The empty path is
              "not declared", which is what lets each response be read under
              its own key — and the count is every Feed's, not one report's. */}
          {feedsDisagree && (
            <option value="">
              Each Feed's own list — {discovery.record_count.toLocaleString()}{" "}
              {discovery.record_count === 1 ? "entry" : "entries"} across{" "}
              {discovery.feeds.length} Feeds
            </option>
          )}
          {candidates.map((candidate) => (
            <option key={candidate.path} value={candidate.path}>
              {candidate.path || "The document root"} — {candidate.record_count.toLocaleString()}{" "}
              {candidate.record_count === 1 ? "entry" : "entries"}
              {/* Whose count this is, where it is one Feed's out of several.
                  "447 entries" beside a 5,500-player division reads as the
                  source being wrong rather than as the first report's total. */}
              {feedsDisagree && ` — ${discovery.feeds[0]?.label ?? "the first Feed"} only`}
            </option>
          ))}
          <option value={TYPED_PATH}>Another path…</option>
        </Select>
      )}
      {showInput && (
        <TextInput
          value={recordPath}
          placeholder={discovery.array_path}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            if (recordPath !== "") onPick(recordPath);
          }}
        />
      )}
    </div>
  );
}
